import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { normalizeAuditHostname } from "@/lib/audit-slug";
import { assertPublicFormSecurity } from "@/lib/form-security.server";
import {
  LEADERBOARD_MIN_AEO_SCORE,
  LEADERBOARD_TOP_N,
  type LeaderboardData,
  type LeaderboardEntry,
} from "@/lib/leaderboard";

// ---------- Types shared with the client ----------
export type CheckStatus = "pass" | "warning" | "fail";
export type DataQuality = "full" | "partial" | "fallback";

export interface AuditCheck {
  id: string;
  label: string;
  status: CheckStatus;
  message: string;
  weight: number;
}

export interface AuditMetric {
  id: string;
  label: string;
  value: string;
  status: CheckStatus | "info";
  hint?: string;
}

export interface AuditOpportunity {
  id: string;
  title: string;
  description: string;
  impact: "high" | "medium" | "low";
  savingsMs?: number;
  savingsBytes?: number;
}

export interface AuditFinding {
  id: string;
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface AuditResult {
  storeUrl: string;
  overallScore: number;
  checks: AuditCheck[];
  summary: string;
  metrics: AuditMetric[];
  opportunities: AuditOpportunity[];
  seoFindings: AuditFinding[];
  conversionFindings: AuditFinding[];
  dataQuality: DataQuality;
  dataNote?: string;
  detectedPlatform: DetectedPlatform;
}

// ---------- Localized copy ----------
type Copy = { pass: string; warning: string; fail: string };
type Strings = {
  labels: Record<string, string>;
  messages: Record<string, Copy>;
  metrics: Record<string, string>;
  errors: { unreachable: string; invalid: string; noKey: string };
  notes: { partial: string; fallback: string; full: string };
  summary: { good: string; ok: string; bad: string; fallback: string };
  opportunities: Record<string, { title: string; description: string }>;
  seo: Record<string, { label: string; pass: string; fail: string }>;
  cro: Record<string, { label: string; pass: string; fail: string; warning: string }>;
};

const STRINGS_EN: Strings = {
  labels: {
    mobilePerf: "Mobile Performance Score",
    desktopPerf: "Desktop Performance Score",
    lcp: "Largest Contentful Paint (LCP)",
    seo: "SEO Score",
    a11y: "Accessibility Score",
    https: "HTTPS / Secure Connection",
    rbr: "Render-Blocking Resources",
    images: "Image Optimization",
  },
  messages: {
    mobilePerf: {
      pass: "Your mobile store loads fast. This directly supports conversion on phones.",
      warning: "Mobile performance needs work. Slow loads cost you sales, especially on phones.",
      fail: "Critical mobile performance issues detected. Stores this slow lose a significant share of mobile revenue.",
    },
    desktopPerf: {
      pass: "Your desktop store loads quickly. Customers see your storefront without waiting.",
      warning: "Desktop performance needs work. Slow loads cost conversion on every visit.",
      fail: "Critical desktop performance issues detected. This level of slowness directly hurts revenue.",
    },
    lcp: {
      pass: "LCP is within Google's recommended range. Your main content loads quickly.",
      warning:
        "LCP is in the needs-improvement range. Customers wait longer than they should for your page to feel ready.",
      fail: "LCP is poor. Google flags this as a Core Web Vital failure, which affects both rankings and conversion.",
    },
    seo: {
      pass: "Strong SEO signals. Your store is well-configured for search engines.",
      warning: "Some SEO issues detected. These are fixable and directly affect organic traffic.",
      fail: "Significant SEO gaps found. These are likely costing you organic visibility.",
    },
    a11y: {
      pass: "Accessibility is strong. Your store works for customers with different needs and devices.",
      warning: "Accessibility gaps detected. These also affect SEO and can exclude customers.",
      fail: "Significant accessibility issues. These hurt both usability and search engine trust.",
    },
    https: {
      pass: "Your store runs on HTTPS. This is a baseline requirement for Shopify and for Google trust signals.",
      warning: "",
      fail: "Your store URL does not appear to be using HTTPS. This is a critical issue for security and SEO.",
    },
    rbr: {
      pass: "No render-blocking resources detected. Your store loads without unnecessary delays.",
      warning:
        "Some render-blocking resources found. These slow down how quickly customers see your storefront.",
      fail: "Significant render-blocking resources detected. This is one of the most common causes of slow Shopify stores.",
    },
    images: {
      pass: "Images are well optimized. No unnecessary file size overhead detected.",
      warning: "Some images could be better optimized. This adds unnecessary load time.",
      fail: "Image optimization issues detected. Uncompressed images are one of the top causes of slow Shopify stores.",
    },
  },
  metrics: {
    lcp: "Largest Contentful Paint",
    cls: "Cumulative Layout Shift",
    tbt: "Total Blocking Time",
    fcp: "First Contentful Paint",
    si: "Speed Index",
    perfMobile: "Performance (Mobile)",
    perfDesktop: "Performance (Desktop)",
    seo: "SEO",
    a11y: "Accessibility",
    bestPractices: "Best Practices",
    pageWeight: "Total Page Weight",
    requests: "Network Requests",
    domSize: "DOM Elements",
    thirdParty: "Third-party Weight",
  },
  errors: {
    unreachable: "We couldn't reach this URL. Make sure it's a live public store and try again.",
    invalid: "Please enter a full URL starting with https://",
    noKey: "The audit service is temporarily unavailable. Please try again shortly.",
  },
  notes: {
    full: "Full Lighthouse data from Google PageSpeed Insights.",
    partial:
      "Mobile or desktop data was unavailable. The visible report uses what Google returned in time.",
    fallback: "We ran a direct inspection of your store's public signals. Here's what we found.",
  },
  summary: {
    good: "Your store is in solid shape. There are still revenue-grade improvements you can ship.",
    ok: "Your store has clear room to grow. The fixes below are typical wins for Shopify stores at this stage.",
    bad: "Your store has performance and SEO issues that almost certainly cost revenue today.",
    fallback: "We ran a direct inspection of your store's public signals. Here's what we found.",
  },
  opportunities: {
    "unused-javascript": {
      title: "Remove unused JavaScript",
      description: "JS your customers download and never run. Common from old apps and themes.",
    },
    "unused-css-rules": {
      title: "Remove unused CSS",
      description: "Stylesheets shipped to every page but only used on a few.",
    },
    "render-blocking-resources": {
      title: "Eliminate render-blocking resources",
      description: "Scripts and CSS that delay first paint on every page.",
    },
    "uses-optimized-images": {
      title: "Serve optimized images",
      description: "Compress and re-encode large images that aren't pulling their weight.",
    },
    "modern-image-formats": {
      title: "Serve images in modern formats (WebP/AVIF)",
      description: "Modern formats can cut image weight by 25–50% with no quality loss.",
    },
    "uses-responsive-images": {
      title: "Properly size images",
      description: "You're sending desktop-sized images to phones.",
    },
    "offscreen-images": {
      title: "Lazy-load offscreen images",
      description: "Defer images below the fold so the first screen paints faster.",
    },
    "third-party-summary": {
      title: "Reduce third-party impact",
      description:
        "Apps and scripts from outside your domain are the #1 cause of Shopify slowness.",
    },
    "uses-text-compression": {
      title: "Enable text compression",
      description: "Compressing HTML, JS and CSS shrinks transfer size for every visit.",
    },
    "uses-long-cache-ttl": {
      title: "Use long cache lifetimes",
      description: "Repeat visitors should not re-download static assets.",
    },
    "dom-size": {
      title: "Reduce DOM size",
      description: "Excessive DOM elements slow rendering, scrolling, and interaction.",
    },
    "server-response-time": {
      title: "Improve server response time",
      description: "Slow first byte holds back every other Lighthouse metric.",
    },
    "total-byte-weight": {
      title: "Reduce total page weight",
      description: "Heavy pages punish mobile shoppers and conversion.",
    },
    "uses-rel-preconnect": {
      title: "Preconnect to required origins",
      description: "Warm up connections to fonts, payment, and analytics origins.",
    },
    "efficient-animated-content": {
      title: "Use efficient video formats",
      description: "Replace heavy GIFs with MP4/WebM.",
    },
    "legacy-javascript": {
      title: "Avoid serving legacy JavaScript to modern browsers",
      description: "Modern browsers don't need ES5 polyfills.",
    },
  },
  seo: {
    title: {
      label: "Page title",
      pass: "Your homepage has a meaningful <title>.",
      fail: "Missing or empty <title>.",
    },
    description: {
      label: "Meta description",
      pass: "A meta description is set.",
      fail: "Missing meta description.",
    },
    canonical: {
      label: "Canonical tag",
      pass: "Canonical tag is set.",
      fail: "No canonical tag found.",
    },
    h1: { label: "H1 heading", pass: "An H1 is present.", fail: "No H1 found on the homepage." },
    viewport: {
      label: "Mobile viewport tag",
      pass: "Mobile viewport meta tag is set.",
      fail: "Missing viewport meta tag.",
    },
    og: {
      label: "Open Graph tags",
      pass: "Open Graph tags are set.",
      fail: "Missing og:title / og:image: link previews will look bare.",
    },
    structured: {
      label: "Structured data",
      pass: "Structured data (JSON-LD) detected.",
      fail: "No JSON-LD structured data detected.",
    },
    robots: {
      label: "Indexable",
      pass: "Page is indexable by search engines.",
      fail: "Page may be blocked from indexing (noindex / robots).",
    },
  },
  cro: {
    mobileSpeed: {
      label: "Mobile shopping experience",
      pass: "Mobile loads fast enough to retain shoppers.",
      warning: "Mobile is borderline, checkout abandonment risk.",
      fail: "Mobile is too slow for paid traffic to convert reliably.",
    },
    imageWeight: {
      label: "Product image strategy",
      pass: "Images won't drag down product pages.",
      warning: "Images add measurable friction on mobile.",
      fail: "Image weight is a primary conversion killer here.",
    },
    thirdParty: {
      label: "Third-party app load",
      pass: "Third-party scripts are under control.",
      warning: "Third-party scripts are noticeably slowing the store.",
      fail: "Third-party apps and tags dominate load time.",
    },
    accessibility: {
      label: "Inclusive shopping",
      pass: "Most shoppers can navigate without friction.",
      warning: "Accessibility gaps will exclude part of your audience.",
      fail: "Significant accessibility issues, both UX and SEO suffer.",
    },
    seo: {
      label: "Organic visibility",
      pass: "Search engines can understand and rank your store.",
      warning: "Some SEO basics are missing, easy wins on the table.",
      fail: "SEO gaps will keep you from showing up for buying-intent searches.",
    },
  },
};

const STRINGS_ES: Strings = {
  labels: {
    mobilePerf: "Rendimiento en móvil",
    desktopPerf: "Rendimiento en desktop",
    lcp: "Largest Contentful Paint (LCP)",
    seo: "Puntuación SEO",
    a11y: "Accesibilidad",
    https: "HTTPS / Conexión segura",
    rbr: "Recursos que bloquean el renderizado",
    images: "Optimización de imágenes",
  },
  messages: {
    mobilePerf: {
      pass: "Tu tienda carga rápido en móvil. Esto impulsa directamente la conversión en celulares.",
      warning:
        "El rendimiento móvil necesita trabajo. Las cargas lentas te cuestan ventas, sobre todo en celular.",
      fail: "Problemas críticos de rendimiento móvil. Tiendas tan lentas pierden una parte significativa de los ingresos móviles.",
    },
    desktopPerf: {
      pass: "Tu tienda carga rápido en desktop. Los clientes ven tu storefront sin esperar.",
      warning:
        "El rendimiento en desktop necesita trabajo. Las cargas lentas afectan la conversión.",
      fail: "Problemas críticos de rendimiento en desktop. Este nivel de lentitud impacta directamente los ingresos.",
    },
    lcp: {
      pass: "El LCP está dentro del rango recomendado por Google. Tu contenido principal carga rápido.",
      warning:
        "El LCP está en el rango de 'necesita mejorar'. Los clientes esperan más de la cuenta para ver tu página lista.",
      fail: "LCP deficiente. Google lo marca como un fallo de Core Web Vitals, lo que afecta posicionamiento y conversión.",
    },
    seo: {
      pass: "Señales SEO sólidas. Tu tienda está bien configurada para los motores de búsqueda.",
      warning:
        "Se detectaron problemas SEO. Son corregibles y afectan directamente al tráfico orgánico.",
      fail: "Se encontraron brechas SEO importantes. Probablemente te están costando visibilidad orgánica.",
    },
    a11y: {
      pass: "La accesibilidad es sólida. Tu tienda funciona para clientes con diferentes necesidades y dispositivos.",
      warning:
        "Brechas de accesibilidad detectadas. También afectan al SEO y pueden excluir clientes.",
      fail: "Problemas significativos de accesibilidad. Afectan la usabilidad y la confianza ante buscadores.",
    },
    https: {
      pass: "Tu tienda corre sobre HTTPS. Es un requisito básico para Shopify y para las señales de confianza de Google.",
      warning: "",
      fail: "Tu URL no parece usar HTTPS. Es un problema crítico de seguridad y SEO.",
    },
    rbr: {
      pass: "No se detectaron recursos que bloqueen el renderizado. Tu tienda carga sin demoras innecesarias.",
      warning:
        "Se encontraron algunos recursos que bloquean el renderizado. Retrasan cuándo los clientes ven tu storefront.",
      fail: "Se detectaron recursos significativos que bloquean el renderizado. Es una de las causas más comunes de tiendas Shopify lentas.",
    },
    images: {
      pass: "Las imágenes están bien optimizadas. Sin sobrepeso innecesario.",
      warning: "Algunas imágenes podrían optimizarse mejor. Suman tiempo de carga innecesario.",
      fail: "Problemas de optimización de imágenes. Las imágenes sin comprimir son una de las principales causas de tiendas lentas.",
    },
  },
  metrics: {
    lcp: "Largest Contentful Paint",
    cls: "Cumulative Layout Shift",
    tbt: "Total Blocking Time",
    fcp: "First Contentful Paint",
    si: "Speed Index",
    perfMobile: "Rendimiento (móvil)",
    perfDesktop: "Rendimiento (desktop)",
    seo: "SEO",
    a11y: "Accesibilidad",
    bestPractices: "Buenas prácticas",
    pageWeight: "Peso total de la página",
    requests: "Peticiones de red",
    domSize: "Elementos DOM",
    thirdParty: "Peso de terceros",
  },
  errors: {
    unreachable:
      "No pudimos acceder a esta URL. Asegúrate de que sea una tienda pública en vivo e inténtalo de nuevo.",
    invalid: "Ingresa una URL completa que comience con https://",
    noKey:
      "El servicio de auditoría está temporalmente no disponible. Inténtalo de nuevo en un momento.",
  },
  notes: {
    full: "Datos completos de Lighthouse vía Google PageSpeed Insights.",
    partial:
      "No tuvimos datos de móvil o desktop completos. El reporte usa lo que Google devolvió a tiempo.",
    fallback:
      "Hicimos una inspección directa de las señales públicas de tu tienda. Esto es lo que encontramos.",
  },
  summary: {
    good: "Tu tienda está en buena forma. Aún hay mejoras de impacto en ingresos por implementar.",
    ok: "Tu tienda tiene espacio claro para crecer. Los fixes abajo son ganancias típicas para tiendas Shopify en esta etapa.",
    bad: "Tu tienda tiene problemas de rendimiento y SEO que casi seguro hoy te están costando ingresos.",
    fallback:
      "Hicimos una inspección directa de las señales públicas de tu tienda. Esto es lo que encontramos.",
  },
  opportunities: {
    "unused-javascript": {
      title: "Eliminar JavaScript no usado",
      description:
        "JS que tus clientes descargan pero nunca ejecutan. Común en apps y temas viejos.",
    },
    "unused-css-rules": {
      title: "Eliminar CSS no usado",
      description: "Hojas de estilo enviadas a cada página pero usadas solo en algunas.",
    },
    "render-blocking-resources": {
      title: "Eliminar recursos que bloquean el render",
      description: "Scripts y CSS que retrasan el primer pintado en cada página.",
    },
    "uses-optimized-images": {
      title: "Servir imágenes optimizadas",
      description: "Comprime y recodifica imágenes grandes que no aportan valor.",
    },
    "modern-image-formats": {
      title: "Servir imágenes en formatos modernos (WebP/AVIF)",
      description: "Pueden reducir 25–50% el peso sin pérdida visible.",
    },
    "uses-responsive-images": {
      title: "Dimensionar imágenes correctamente",
      description: "Estás enviando imágenes de desktop a celulares.",
    },
    "offscreen-images": {
      title: "Lazy-load de imágenes fuera de pantalla",
      description: "Aplaza imágenes debajo del fold para que la primera pantalla pinte más rápido.",
    },
    "third-party-summary": {
      title: "Reducir el impacto de terceros",
      description: "Apps y scripts externos son la #1 causa de lentitud en Shopify.",
    },
    "uses-text-compression": {
      title: "Activar compresión de texto",
      description: "Comprimir HTML, JS y CSS reduce el tamaño en cada visita.",
    },
    "uses-long-cache-ttl": {
      title: "Usar TTLs de caché largos",
      description: "Visitantes recurrentes no deberían volver a descargar assets estáticos.",
    },
    "dom-size": {
      title: "Reducir el tamaño del DOM",
      description: "Demasiados elementos DOM ralentizan render, scroll e interacción.",
    },
    "server-response-time": {
      title: "Mejorar tiempo de respuesta del servidor",
      description: "Un TTFB lento limita todas las demás métricas.",
    },
    "total-byte-weight": {
      title: "Reducir peso total de la página",
      description: "Páginas pesadas castigan a compradores en móvil y conversión.",
    },
    "uses-rel-preconnect": {
      title: "Preconnect a orígenes necesarios",
      description: "Calienta conexiones a fuentes, pagos y analítica.",
    },
    "efficient-animated-content": {
      title: "Usar formatos de video eficientes",
      description: "Reemplaza GIFs pesados por MP4/WebM.",
    },
    "legacy-javascript": {
      title: "No servir JavaScript legado a navegadores modernos",
      description: "Los navegadores modernos no necesitan polyfills ES5.",
    },
  },
  seo: {
    title: {
      label: "Title de la página",
      pass: "Tu home tiene un <title> con sentido.",
      fail: "Falta o está vacío el <title>.",
    },
    description: {
      label: "Meta description",
      pass: "Hay meta description configurada.",
      fail: "Falta meta description.",
    },
    canonical: {
      label: "Canonical",
      pass: "Etiqueta canonical configurada.",
      fail: "No se encontró canonical.",
    },
    h1: {
      label: "Encabezado H1",
      pass: "Hay un H1 en la página.",
      fail: "No se encontró H1 en la home.",
    },
    viewport: {
      label: "Viewport móvil",
      pass: "Meta viewport móvil presente.",
      fail: "Falta meta viewport.",
    },
    og: {
      label: "Open Graph",
      pass: "Open Graph configurado.",
      fail: "Faltan og:title / og:image: los previews de enlace se verán pobres.",
    },
    structured: {
      label: "Datos estructurados",
      pass: "Datos estructurados (JSON-LD) detectados.",
      fail: "No se detectó JSON-LD.",
    },
    robots: {
      label: "Indexable",
      pass: "La página es indexable por buscadores.",
      fail: "La página podría estar bloqueada (noindex / robots).",
    },
  },
  cro: {
    mobileSpeed: {
      label: "Experiencia móvil de compra",
      pass: "Móvil carga lo suficiente para retener compradores.",
      warning: "Móvil al límite, riesgo de abandono en checkout.",
      fail: "Móvil demasiado lento para que tráfico pagado convierta.",
    },
    imageWeight: {
      label: "Estrategia de imágenes de producto",
      pass: "Las imágenes no arrastran las páginas de producto.",
      warning: "Las imágenes suman fricción medible en móvil.",
      fail: "El peso de imágenes es un asesino primario de conversión aquí.",
    },
    thirdParty: {
      label: "Carga de apps de terceros",
      pass: "Los scripts de terceros están bajo control.",
      warning: "Los scripts de terceros están ralentizando notablemente la tienda.",
      fail: "Apps y tags de terceros dominan el tiempo de carga.",
    },
    accessibility: {
      label: "Compra inclusiva",
      pass: "La mayoría de los compradores puede navegar sin fricción.",
      warning: "Brechas de accesibilidad excluirán parte de tu audiencia.",
      fail: "Problemas serios de accesibilidad, sufre la UX y el SEO.",
    },
    seo: {
      label: "Visibilidad orgánica",
      pass: "Los buscadores pueden entender y posicionar tu tienda.",
      warning: "Faltan básicos de SEO, wins fáciles a la mano.",
      fail: "Las brechas de SEO te dejarán fuera de búsquedas con intención de compra.",
    },
  },
};

function getStrings(locale: "en" | "es"): Strings {
  return locale === "es" ? STRINGS_ES : STRINGS_EN;
}

// ---------- Lighthouse type ----------
type LhAudit = {
  score?: number | null;
  numericValue?: number;
  displayValue?: string;
  details?: { overallSavingsMs?: number; overallSavingsBytes?: number; items?: unknown[] };
};
type Lh = {
  categories?: {
    performance?: { score?: number | null };
    seo?: { score?: number | null };
    accessibility?: { score?: number | null };
    "best-practices"?: { score?: number | null };
  };
  audits?: Record<string, LhAudit>;
};

function getLh(json: unknown): Lh | null {
  if (
    json &&
    typeof json === "object" &&
    "lighthouseResult" in json &&
    (json as { lighthouseResult: unknown }).lighthouseResult
  ) {
    return (json as { lighthouseResult: Lh }).lighthouseResult;
  }
  return null;
}

function bandPerf(score: number, thresholds: [number, number]): CheckStatus {
  if (score >= thresholds[0]) return "pass";
  if (score >= thresholds[1]) return "warning";
  return "fail";
}

function detailSuffix(locale: "en" | "es", measured: string, thresholdLine: string): string {
  return locale === "es"
    ? ` Medido: ${measured}. Umbral: ${thresholdLine}.`
    : ` Measured: ${measured}. Threshold: ${thresholdLine}.`;
}

const RECOMMENDATIONS: Record<
  "mobilePerf" | "lcp" | "rbr" | "images",
  { en: string[]; es: string[] }
> = {
  mobilePerf: {
    en: [
      'Ship a hero image sized for mobile (≤ 440px wide, WebP/AVIF) and set fetchpriority="high" on it.',
      "Defer or lazy-load non-critical third-party apps (chat, reviews, upsell) until user interaction or idle.",
      "Audit the top 3 heaviest Shopify apps in the theme editor and remove any that aren't producing measurable revenue.",
      "Preconnect to critical origins (CDN, fonts, analytics) and remove unused ones from theme.liquid.",
    ],
    es: [
      'Sirve una imagen hero optimizada para móvil (≤ 440px de ancho, WebP/AVIF) y márcala con fetchpriority="high".',
      "Difiere o carga con lazy-load las apps de terceros no críticas (chat, reseñas, upsell) hasta interacción o idle.",
      "Revisa en el editor de temas las 3 apps más pesadas y elimina las que no generen ingresos medibles.",
      "Preconnect a los orígenes críticos (CDN, fuentes, analítica) y quita los que no uses de theme.liquid.",
    ],
  },
  lcp: {
    en: [
      'Identify the LCP element in Chrome DevTools > Performance and preload it with <link rel="preload" as="image" fetchpriority="high">.',
      "Serve the LCP image responsive (srcset + sizes) so mobile doesn't download the desktop-sized asset.",
      "Remove render-blocking scripts above the fold; move analytics/heatmap tags to defer or after-load.",
      "If TTFB is high, enable Shopify's built-in CDN caching and reduce Liquid work in the section that renders the LCP element.",
    ],
    es: [
      'Identifica el elemento LCP en Chrome DevTools > Performance y precárgalo con <link rel="preload" as="image" fetchpriority="high">.',
      "Sirve la imagen LCP en versión responsive (srcset + sizes) para que móvil no descargue la versión de desktop.",
      "Elimina scripts que bloquean el render arriba del pliegue; pasa analítica/heatmaps a defer o carga posterior.",
      "Si el TTFB es alto, activa el caching de la CDN de Shopify y reduce el trabajo Liquid en la sección que renderiza el LCP.",
    ],
  },
  rbr: {
    en: [
      "Move non-critical <script> tags to defer or async; inline only the minimum critical JS needed for first paint.",
      'Split the main stylesheet: inline critical CSS in <head> and load the rest with media="print" swap or after-load.',
      "Audit apps injecting head scripts (reviews, popups, A/B tools) and switch to their async/lazy variants.",
      "Remove unused font weights and self-host webfonts with font-display: swap.",
    ],
    es: [
      "Pasa los <script> no críticos a defer o async; inlinea solo el JS crítico mínimo para el primer render.",
      'Divide el CSS principal: inlinea el crítico en <head> y carga el resto con media="print" swap o después de load.',
      "Revisa apps que inyectan scripts en head (reseñas, popups, A/B) y usa sus variantes async/lazy.",
      "Elimina pesos de fuente que no usas y auto-hospeda las webfonts con font-display: swap.",
    ],
  },
  images: {
    en: [
      "Convert product/collection images to WebP or AVIF (Shopify's img_url supports format: 'webp').",
      "Serve responsive images with srcset covering 320w / 640w / 1024w / 1600w and correct sizes attribute.",
      "Set explicit width/height on <img> tags to prevent layout shift and let the browser skip oversized downloads.",
      'Add loading="lazy" to below-the-fold images and loading="eager" fetchpriority="high" only on the LCP image.',
    ],
    es: [
      "Convierte imágenes de producto/colección a WebP o AVIF (img_url de Shopify acepta format: 'webp').",
      "Sirve imágenes responsive con srcset cubriendo 320w / 640w / 1024w / 1600w y el atributo sizes correcto.",
      "Define width/height explícitos en las <img> para evitar layout shift y que el navegador no baje versiones enormes.",
      'Añade loading="lazy" a las imágenes bajo el pliegue y usa loading="eager" con fetchpriority="high" solo en la imagen LCP.',
    ],
  },
};

function recommendationSuffix(
  id: "mobilePerf" | "lcp" | "rbr" | "images",
  status: CheckStatus,
  locale: "en" | "es",
): string {
  if (status === "pass") return "";
  const items = RECOMMENDATIONS[id][locale];
  const heading = locale === "es" ? "Cómo mejorarlo" : "How to improve";
  return `\n\n${heading}:\n- ${items.join("\n- ")}`;
}

function buildHttpsCheck(url: string, s: Strings): AuditCheck {
  const isHttps = url.toLowerCase().startsWith("https://");
  return {
    id: "https",
    label: s.labels.https,
    status: isHttps ? "pass" : "fail",
    message: isHttps ? s.messages.https.pass : s.messages.https.fail,
    weight: 10,
  };
}

function buildPerfCheck(
  id: "mobilePerf" | "desktopPerf",
  lh: Lh,
  s: Strings,
  weight: number,
  thresholds: [number, number],
  locale: "en" | "es",
): AuditCheck {
  const raw = lh.categories?.performance?.score ?? 0;
  const score = Math.round((raw ?? 0) * 100);
  const status = bandPerf(score, thresholds);
  const thresholdLine =
    locale === "es"
      ? `pass ≥ ${thresholds[0]}, warning ≥ ${thresholds[1]}, fail < ${thresholds[1]}`
      : `pass ≥ ${thresholds[0]}, warning ≥ ${thresholds[1]}, fail < ${thresholds[1]}`;
  const recs = id === "mobilePerf" ? recommendationSuffix("mobilePerf", status, locale) : "";
  const message =
    s.messages[id][status] + detailSuffix(locale, `${score}/100`, thresholdLine) + recs;
  return { id, label: s.labels[id], status, message, weight };
}

function buildLcpCheck(lh: Lh, s: Strings, locale: "en" | "es"): AuditCheck {
  const ms = lh.audits?.["largest-contentful-paint"]?.numericValue ?? 0;
  let status: CheckStatus = "fail";
  if (ms < 3000) status = "pass";
  else if (ms < 4500) status = "warning";
  const seconds = (ms / 1000).toFixed(2);
  const thresholdLine =
    locale === "es"
      ? "pass < 3.0 s, warning < 4.5 s, fail ≥ 4.5 s"
      : "pass < 3.0 s, warning < 4.5 s, fail ≥ 4.5 s";
  const measured = locale === "es" ? `${seconds} s` : `${seconds} s`;
  const message =
    s.messages.lcp[status] +
    detailSuffix(locale, measured, thresholdLine) +
    recommendationSuffix("lcp", status, locale);
  return { id: "lcp", label: s.labels.lcp, status, message, weight: 15 };
}

function buildCategoryCheck(
  id: "seo" | "a11y",
  category: "seo" | "accessibility",
  lh: Lh,
  s: Strings,
  weight: number,
): AuditCheck {
  const raw = lh.categories?.[category]?.score ?? 0;
  const score = raw ?? 0;
  let status: CheckStatus = "fail";
  if (score >= 0.9) status = "pass";
  else if (score >= 0.7) status = "warning";
  return { id, label: s.labels[id], status, message: s.messages[id][status], weight };
}

function buildAuditScoreCheck(
  id: "rbr" | "images",
  auditKey: string,
  lh: Lh,
  s: Strings,
  weight: number,
  locale: "en" | "es",
): AuditCheck {
  const score = lh.audits?.[auditKey]?.score ?? 0;
  let status: CheckStatus = "fail";
  if ((score ?? 0) >= 0.9) status = "pass";
  else if ((score ?? 0) >= 0.5) status = "warning";
  const pct = Math.round(((score ?? 0) as number) * 100);
  const savingsMs = lh.audits?.[auditKey]?.numericValue;
  const measuredParts = [`${pct}/100`];
  if (typeof savingsMs === "number" && savingsMs > 0) {
    measuredParts.push(
      locale === "es"
        ? `${Math.round(savingsMs)} ms de ahorro potencial`
        : `${Math.round(savingsMs)} ms potential savings`,
    );
  }
  const thresholdLine = "pass ≥ 90/100, warning ≥ 50/100, fail < 50/100";
  const message =
    s.messages[id][status] +
    detailSuffix(locale, measuredParts.join(" · "), thresholdLine) +
    recommendationSuffix(id, status, locale);
  return { id, label: s.labels[id], status, message, weight };
}

function calcOverall(checks: AuditCheck[]): number {
  const points = { pass: 100, warning: 50, fail: 0 } as const;
  const totalWeight = checks.reduce((sum, c) => sum + c.weight, 0);
  if (!totalWeight) return 0;
  const weighted = checks.reduce((sum, c) => sum + c.weight * points[c.status], 0);
  return Math.round(weighted / totalWeight);
}

// ---------- Robust PSI fetch with retry ----------
async function fetchPSIOnce(
  url: string,
  strategy: "mobile" | "desktop",
  apiKey: string,
  timeoutMs: number,
): Promise<unknown> {
  const psiUrl = new URL("https://www.googleapis.com/pagespeedonline/v5/runPagespeed");
  psiUrl.searchParams.set("url", url);
  psiUrl.searchParams.set("strategy", strategy);
  psiUrl.searchParams.set("key", apiKey);
  for (const cat of ["performance", "seo", "accessibility", "best-practices"]) {
    psiUrl.searchParams.append("category", cat);
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(psiUrl.toString(), {
      signal: controller.signal,
      headers: {
        Referer: "https://shugert.com.mx/",
        "User-Agent": "ShugertAuditBot/1.0 (+https://shugert.com.mx)",
      },
    });
    if (!res.ok) throw new Error(`PSI ${strategy} HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

async function fetchPSI(
  url: string,
  strategy: "mobile" | "desktop",
  apiKey: string,
): Promise<unknown> {
  const attempts = [55_000, 50_000];
  let lastErr: unknown;
  for (let i = 0; i < attempts.length; i++) {
    try {
      return await fetchPSIOnce(url, strategy, apiKey, attempts[i]);
    } catch (err) {
      lastErr = err;
      console.warn(
        `PSI ${strategy} attempt ${i + 1} failed:`,
        err instanceof Error ? err.message : err,
      );
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(`PSI ${strategy} failed`);
}

function normalizeUrl(input: string): string | null {
  try {
    const trimmed = input.trim();
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const u = new URL(withProto);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    if (!u.hostname.includes(".")) return null;
    // Block private/loopback/link-local/reserved IP ranges (SSRF guard)
    const host = u.hostname.toLowerCase();
    if (
      host === "localhost" ||
      host === "0.0.0.0" ||
      /^127\./.test(host) ||
      /^10\./.test(host) ||
      /^192\.168\./.test(host) ||
      /^169\.254\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
      /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(host) ||
      /^(::1|fc00:|fd00:|fe80:)/i.test(host) ||
      host.endsWith(".internal") ||
      host.endsWith(".local")
    ) {
      return null;
    }
    return u.toString();
  } catch {
    return null;
  }
}

// ---------- Metrics, opportunities, findings ----------
function fmtMs(ms?: number): string {
  if (ms === undefined || ms === null || Number.isNaN(ms)) return ", ";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}
function fmtBytes(bytes?: number): string {
  if (!bytes) return ", ";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function metricStatus(
  value: number | undefined,
  good: number,
  poor: number,
  lowerIsBetter = true,
): CheckStatus | "info" {
  if (value === undefined || value === null) return "info";
  if (lowerIsBetter) {
    if (value <= good) return "pass";
    if (value <= poor) return "warning";
    return "fail";
  }
  if (value >= good) return "pass";
  if (value >= poor) return "warning";
  return "fail";
}

function buildMetrics(mobile: Lh | null, desktop: Lh | null, s: Strings): AuditMetric[] {
  const m: AuditMetric[] = [];
  const mobilePerf = mobile?.categories?.performance?.score;
  const desktopPerf = desktop?.categories?.performance?.score;
  if (mobilePerf !== undefined && mobilePerf !== null) {
    const v = Math.round(mobilePerf * 100);
    m.push({
      id: "perfMobile",
      label: s.metrics.perfMobile,
      value: `${v}/100`,
      status: v >= 70 ? "pass" : v >= 50 ? "warning" : "fail",
    });
  }
  if (desktopPerf !== undefined && desktopPerf !== null) {
    const v = Math.round(desktopPerf * 100);
    m.push({
      id: "perfDesktop",
      label: s.metrics.perfDesktop,
      value: `${v}/100`,
      status: v >= 85 ? "pass" : v >= 65 ? "warning" : "fail",
    });
  }
  const lh = mobile ?? desktop;
  if (lh) {
    const lcp = lh.audits?.["largest-contentful-paint"]?.numericValue;
    if (lcp !== undefined)
      m.push({
        id: "lcp",
        label: s.metrics.lcp,
        value: fmtMs(lcp),
        status: metricStatus(lcp, 2500, 4000),
      });
    const cls = lh.audits?.["cumulative-layout-shift"]?.numericValue;
    if (cls !== undefined)
      m.push({
        id: "cls",
        label: s.metrics.cls,
        value: cls.toFixed(3),
        status: metricStatus(cls, 0.1, 0.25),
      });
    const tbt = lh.audits?.["total-blocking-time"]?.numericValue;
    if (tbt !== undefined)
      m.push({
        id: "tbt",
        label: s.metrics.tbt,
        value: fmtMs(tbt),
        status: metricStatus(tbt, 200, 600),
      });
    const fcp = lh.audits?.["first-contentful-paint"]?.numericValue;
    if (fcp !== undefined)
      m.push({
        id: "fcp",
        label: s.metrics.fcp,
        value: fmtMs(fcp),
        status: metricStatus(fcp, 1800, 3000),
      });
    const si = lh.audits?.["speed-index"]?.numericValue;
    if (si !== undefined)
      m.push({
        id: "si",
        label: s.metrics.si,
        value: fmtMs(si),
        status: metricStatus(si, 3400, 5800),
      });
    const seo = lh.categories?.seo?.score;
    if (seo !== undefined && seo !== null) {
      const v = Math.round(seo * 100);
      m.push({
        id: "seo",
        label: s.metrics.seo,
        value: `${v}/100`,
        status: v >= 90 ? "pass" : v >= 70 ? "warning" : "fail",
      });
    }
    const a11y = lh.categories?.accessibility?.score;
    if (a11y !== undefined && a11y !== null) {
      const v = Math.round(a11y * 100);
      m.push({
        id: "a11y",
        label: s.metrics.a11y,
        value: `${v}/100`,
        status: v >= 90 ? "pass" : v >= 70 ? "warning" : "fail",
      });
    }
    const bp = lh.categories?.["best-practices"]?.score;
    if (bp !== undefined && bp !== null) {
      const v = Math.round(bp * 100);
      m.push({
        id: "bestPractices",
        label: s.metrics.bestPractices,
        value: `${v}/100`,
        status: v >= 90 ? "pass" : v >= 70 ? "warning" : "fail",
      });
    }
    const weight = lh.audits?.["total-byte-weight"]?.numericValue;
    if (weight !== undefined)
      m.push({
        id: "pageWeight",
        label: s.metrics.pageWeight,
        value: fmtBytes(weight),
        status: metricStatus(weight, 1_600_000, 4_000_000),
      });
    const dom = lh.audits?.["dom-size"]?.numericValue;
    if (dom !== undefined)
      m.push({
        id: "domSize",
        label: s.metrics.domSize,
        value: String(Math.round(dom)),
        status: metricStatus(dom, 800, 1500),
      });
    const tp = lh.audits?.["third-party-summary"]?.details?.items as
      | Array<{ transferSize?: number }>
      | undefined;
    if (tp && tp.length) {
      const total = tp.reduce((sum, x) => sum + (x.transferSize ?? 0), 0);
      m.push({
        id: "thirdParty",
        label: s.metrics.thirdParty,
        value: fmtBytes(total),
        status: metricStatus(total, 300_000, 800_000),
      });
    }
  }
  return m;
}

const OPPORTUNITY_KEYS = [
  "render-blocking-resources",
  "unused-javascript",
  "unused-css-rules",
  "uses-optimized-images",
  "modern-image-formats",
  "uses-responsive-images",
  "offscreen-images",
  "third-party-summary",
  "uses-text-compression",
  "uses-long-cache-ttl",
  "dom-size",
  "server-response-time",
  "total-byte-weight",
  "uses-rel-preconnect",
  "efficient-animated-content",
  "legacy-javascript",
];

function buildOpportunities(lh: Lh | null, s: Strings): AuditOpportunity[] {
  if (!lh?.audits) return [];
  const out: AuditOpportunity[] = [];
  for (const key of OPPORTUNITY_KEYS) {
    const a = lh.audits[key];
    if (!a) continue;
    const score = a.score ?? 1;
    const savingsMs = a.details?.overallSavingsMs ?? 0;
    const savingsBytes = a.details?.overallSavingsBytes ?? 0;
    if (score >= 0.95 && savingsMs < 100 && savingsBytes < 20_000) continue;
    const meta = s.opportunities[key];
    if (!meta) continue;
    let impact: "high" | "medium" | "low" = "low";
    if (savingsMs >= 1000 || savingsBytes >= 500_000 || score < 0.5) impact = "high";
    else if (savingsMs >= 300 || savingsBytes >= 100_000 || score < 0.9) impact = "medium";
    out.push({
      id: key,
      title: meta.title,
      description: meta.description,
      impact,
      savingsMs: savingsMs || undefined,
      savingsBytes: savingsBytes || undefined,
    });
  }
  out.sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.impact] - order[b.impact] || (b.savingsMs ?? 0) - (a.savingsMs ?? 0);
  });
  return out.slice(0, 8);
}

function buildSeoFindings(lh: Lh | null, html: string | null, s: Strings): AuditFinding[] {
  const out: AuditFinding[] = [];
  const push = (id: keyof Strings["seo"], pass: boolean) => {
    const meta = s.seo[id];
    out.push({
      id: id as string,
      label: meta.label,
      status: pass ? "pass" : "fail",
      detail: pass ? meta.pass : meta.fail,
    });
  };

  // From Lighthouse audits
  if (lh?.audits) {
    push("title", (lh.audits["document-title"]?.score ?? 0) === 1);
    push("description", (lh.audits["meta-description"]?.score ?? 0) === 1);
    push("canonical", (lh.audits["canonical"]?.score ?? 1) === 1);
    const lhViewport = lh.audits["viewport"]?.score;
    const viewportPass =
      lhViewport === 1 ||
      (lhViewport !== 0 && html !== null && /<meta[^>]+name=["']viewport/i.test(html));
    push("viewport", viewportPass);
    push("robots", (lh.audits["is-crawlable"]?.score ?? 1) === 1);
    push(
      "structured",
      (lh.audits["structured-data"]?.score ?? 1) === 1 && Boolean(lh.audits["structured-data"]),
    );
    if (html) {
      push("h1", /<h1\b/i.test(html));
      push(
        "og",
        /<meta[^>]+property=["']og:title/i.test(html) &&
          /<meta[^>]+property=["']og:image/i.test(html),
      );
    }
  } else if (html) {
    // Fallback: parse HTML directly
    push("title", /<title>[^<]{3,}<\/title>/i.test(html));
    push(
      "description",
      /<meta[^>]+name=["']description["'][^>]+content=["'][^"']{10,}/i.test(html),
    );
    push("canonical", /<link[^>]+rel=["']canonical/i.test(html));
    push("viewport", /<meta[^>]+name=["']viewport/i.test(html));
    push("h1", /<h1\b/i.test(html));
    push(
      "og",
      /<meta[^>]+property=["']og:title/i.test(html) &&
        /<meta[^>]+property=["']og:image/i.test(html),
    );
    push("structured", /<script[^>]+type=["']application\/ld\+json/i.test(html));
    push("robots", !/<meta[^>]+name=["']robots["'][^>]+content=["'][^"']*noindex/i.test(html));
  }
  return out;
}

function buildConversionFindings(lh: Lh | null, s: Strings): AuditFinding[] {
  if (!lh) return [];
  const out: AuditFinding[] = [];
  const perf = Math.round((lh.categories?.performance?.score ?? 0) * 100);
  const ms = lh.audits?.["largest-contentful-paint"]?.numericValue ?? 0;
  const mobileSpeedStatus: CheckStatus =
    perf >= 70 && ms < 2500 ? "pass" : perf >= 50 && ms < 4000 ? "warning" : "fail";
  out.push({
    id: "mobileSpeed",
    label: s.cro.mobileSpeed.label,
    status: mobileSpeedStatus,
    detail: s.cro.mobileSpeed[mobileSpeedStatus],
  });

  const imgScore = lh.audits?.["uses-optimized-images"]?.score ?? 1;
  const modernScore = lh.audits?.["modern-image-formats"]?.score ?? 1;
  const imgBlend = Math.min(imgScore, modernScore);
  const imgStatus: CheckStatus = imgBlend === 1 ? "pass" : imgBlend >= 0.5 ? "warning" : "fail";
  out.push({
    id: "imageWeight",
    label: s.cro.imageWeight.label,
    status: imgStatus,
    detail: s.cro.imageWeight[imgStatus],
  });

  const tpItems = (lh.audits?.["third-party-summary"]?.details?.items ?? []) as Array<{
    transferSize?: number;
  }>;
  const tpTotal = tpItems.reduce((a, x) => a + (x.transferSize ?? 0), 0);
  const tpStatus: CheckStatus = tpTotal < 300_000 ? "pass" : tpTotal < 800_000 ? "warning" : "fail";
  out.push({
    id: "thirdParty",
    label: s.cro.thirdParty.label,
    status: tpStatus,
    detail: s.cro.thirdParty[tpStatus],
  });

  const a11y = Math.round((lh.categories?.accessibility?.score ?? 0) * 100);
  const a11yStatus: CheckStatus = a11y >= 90 ? "pass" : a11y >= 70 ? "warning" : "fail";
  out.push({
    id: "accessibility",
    label: s.cro.accessibility.label,
    status: a11yStatus,
    detail: s.cro.accessibility[a11yStatus],
  });

  const seo = Math.round((lh.categories?.seo?.score ?? 0) * 100);
  const seoStatus: CheckStatus = seo >= 90 ? "pass" : seo >= 70 ? "warning" : "fail";
  out.push({ id: "seo", label: s.cro.seo.label, status: seoStatus, detail: s.cro.seo[seoStatus] });

  return out;
}

function buildSummary(score: number, dq: DataQuality, s: Strings): string {
  if (dq === "fallback") return s.summary.fallback;
  if (score >= 80) return s.summary.good;
  if (score >= 60) return s.summary.ok;
  return s.summary.bad;
}

// ---------- Fallback HTML fetch ----------
async function fetchHtml(url: string): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      redirect: "follow",
      headers: {
        "User-Agent": "Mozilla/5.0 (compatible; ShugertAuditBot/1.0; +https://shugert.com.mx)",
        Accept: "text/html,application/xhtml+xml",
      },
    });
    if (!res.ok) return null;
    const text = await res.text();
    return text.slice(0, 600_000);
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export type DetectedPlatform =
  | "shopify"
  | "woocommerce"
  | "wix"
  | "squarespace"
  | "bigcommerce"
  | "magento"
  | "other";

// Heuristic, best-effort platform sniff from the HTML we already fetch for
// SEO findings, no extra request. Good enough to route lead follow-up copy
// and to tag Customer.io profiles for reporting, not a hard guarantee.
function detectPlatform(html: string | null): DetectedPlatform {
  if (!html) return "other";
  const h = html.toLowerCase();
  if (
    h.includes("cdn.shopify.com") ||
    h.includes("cdn.shopifycdn.net") ||
    h.includes("myshopify.com") ||
    h.includes("window.shopify") ||
    h.includes("shopify-section")
  ) {
    return "shopify";
  }
  if (h.includes("woocommerce") || h.includes("wp-json") || h.includes("wp-content"))
    return "woocommerce";
  if (h.includes("static.wixstatic.com") || h.includes("wix.com")) return "wix";
  if (h.includes("squarespace.com") || h.includes("static1.squarespace.com")) return "squarespace";
  if (h.includes("bigcommerce.com") || h.includes("cdn11.bigcommerce.com")) return "bigcommerce";
  if (h.includes("magento") || h.includes("mage/cookies")) return "magento";
  return "other";
}

function buildFallbackChecks(url: string, html: string | null, s: Strings): AuditCheck[] {
  const checks: AuditCheck[] = [buildHttpsCheck(url, s)];
  if (!html) return checks;
  const has = (re: RegExp) => re.test(html);
  const titleOk = has(/<title>[^<]{3,}<\/title>/i);
  const descOk = has(/<meta[^>]+name=["']description["'][^>]+content=["'][^"']{10,}/i);
  const canonicalOk = has(/<link[^>]+rel=["']canonical/i);
  const viewportOk = has(/<meta[^>]+name=["']viewport/i);
  checks.push({
    id: "seo",
    label: s.labels.seo,
    status:
      titleOk && descOk && canonicalOk && viewportOk
        ? "pass"
        : titleOk && descOk
          ? "warning"
          : "fail",
    message: titleOk && descOk && canonicalOk ? s.messages.seo.pass : s.messages.seo.warning,
    weight: 15,
  });
  // image alt coverage
  const imgs = html.match(/<img\b[^>]*>/gi) ?? [];
  const alts = imgs.filter((t) => /\salt\s*=/i.test(t)).length;
  const ratio = imgs.length ? alts / imgs.length : 1;
  checks.push({
    id: "a11y",
    label: s.labels.a11y,
    status: ratio >= 0.9 ? "pass" : ratio >= 0.6 ? "warning" : "fail",
    message:
      ratio >= 0.9
        ? s.messages.a11y.pass
        : ratio >= 0.6
          ? s.messages.a11y.warning
          : s.messages.a11y.fail,
    weight: 10,
  });
  // script weight as a rough perf signal
  const scripts = (html.match(/<script\b/gi) ?? []).length;
  checks.push({
    id: "rbr",
    label: s.labels.rbr,
    status: scripts < 15 ? "pass" : scripts < 30 ? "warning" : "fail",
    message:
      scripts < 15
        ? s.messages.rbr.pass
        : scripts < 30
          ? s.messages.rbr.warning
          : s.messages.rbr.fail,
    weight: 10,
  });
  return checks;
}

// ---------- Server function: run the audit ----------
const AuditInputSchema = z.object({
  url: z.string().trim().min(4).max(500),
  locale: z.enum(["en", "es"]).default("en"),
  runId: z.string().uuid().optional(),
  form_started_at: z.number().int().positive(),
  website_confirmation: z.string().max(200).optional().or(z.literal("")),
  turnstile_token: z.string().max(4000).optional().or(z.literal("")),
});

// Shared by the public runStoreAudit entry point and the admin-gated
// runInternalAuditBatch entry point below, so both run the exact same
// PSI + HTML-fetch + checks/findings pipeline. Security/rate-limiting and
// persistence stay in the two callers, not here.
async function computeStoreAudit(url: string, locale: "en" | "es"): Promise<AuditResult> {
  const s = getStrings(locale);
  const apiKey = process.env.PSI_API_KEY;
  const normalized = normalizeUrl(url);
  if (!normalized) throw new Error(s.errors.invalid);

  let mobileLh: Lh | null = null;
  let desktopLh: Lh | null = null;

  if (apiKey) {
    const [mobile, desktop] = await Promise.allSettled([
      fetchPSI(normalized, "mobile", apiKey),
      fetchPSI(normalized, "desktop", apiKey),
    ]);
    if (mobile.status === "fulfilled") mobileLh = getLh(mobile.value);
    else
      console.warn(
        "PSI mobile permanently failed:",
        mobile.reason instanceof Error ? mobile.reason.message : mobile.reason,
      );
    if (desktop.status === "fulfilled") desktopLh = getLh(desktop.value);
    else
      console.warn(
        "PSI desktop permanently failed:",
        desktop.reason instanceof Error ? desktop.reason.message : desktop.reason,
      );
  } else {
    console.warn("PSI_API_KEY not configured, running fallback audit only");
  }

  // Always grab HTML, used to enrich SEO findings even when PSI succeeds, and as the fallback signal source.
  const html = await fetchHtml(normalized);
  const detectedPlatform = detectPlatform(html);

  const primaryLh = mobileLh ?? desktopLh;
  let dataQuality: DataQuality;
  let dataNote: string | undefined;

  let checks: AuditCheck[];
  if (primaryLh) {
    const baseLh = mobileLh ?? desktopLh!;
    checks = [
      mobileLh
        ? buildPerfCheck("mobilePerf", mobileLh, s, 25, [60, 40], locale)
        : {
            id: "mobilePerf",
            label: s.labels.mobilePerf,
            status: "warning",
            message: s.messages.mobilePerf.warning,
            weight: 25,
          },
      desktopLh
        ? buildPerfCheck("desktopPerf", desktopLh, s, 10, [85, 65], locale)
        : {
            id: "desktopPerf",
            label: s.labels.desktopPerf,
            status: "warning",
            message: s.messages.desktopPerf.warning,
            weight: 10,
          },
      buildLcpCheck(baseLh, s, locale),
      buildCategoryCheck("seo", "seo", baseLh, s, 15),
      buildCategoryCheck("a11y", "accessibility", baseLh, s, 10),
      buildHttpsCheck(normalized, s),
      buildAuditScoreCheck("rbr", "render-blocking-resources", baseLh, s, 10, locale),
      buildAuditScoreCheck("images", "uses-optimized-images", baseLh, s, 5, locale),
    ];
    dataQuality = mobileLh && desktopLh ? "full" : "partial";
    dataNote = dataQuality === "full" ? s.notes.full : s.notes.partial;
  } else {
    checks = buildFallbackChecks(normalized, html, s);
    dataQuality = "fallback";
    dataNote = s.notes.fallback;
  }

  const overallScore = calcOverall(checks);
  const metrics = buildMetrics(mobileLh, desktopLh, s);
  const opportunities = buildOpportunities(primaryLh, s);
  const seoFindings = buildSeoFindings(primaryLh, html, s);
  const conversionFindings = buildConversionFindings(primaryLh, s);
  const summary = buildSummary(overallScore, dataQuality, s);

  const result: AuditResult = {
    storeUrl: normalized,
    overallScore,
    checks,
    summary,
    metrics,
    opportunities,
    seoFindings,
    conversionFindings,
    dataQuality,
    dataNote,
    detectedPlatform,
  };

  return result;
}

export const runStoreAudit = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => AuditInputSchema.parse(input))
  .handler(async ({ data }): Promise<AuditResult> => {
    await assertPublicFormSecurity({
      form: "audit_run",
      input: data,
      minimumMs: 800,
      maxRequests: 6,
      windowMs: 60 * 60 * 1000,
    });
    const result = await computeStoreAudit(data.url, data.locale);

    if (data.runId) {
      await persistAuditRun({
        runId: data.runId,
        inputUrl: data.url,
        normalizedUrl: result.storeUrl,
        locale: data.locale,
        storeScore: result.overallScore,
        storeResult: result as unknown as Record<string, unknown>,
        storeDataQuality: result.dataQuality,
        aeoScore: null,
        aeoResult: null,
        aeoDataQuality: null,
      });
    }

    return result;
  });

// ---------- Server function: internal batch audit (cold outreach) ----------
// Reuses computeStoreAudit, skips assertPublicFormSecurity entirely (this is
// never reachable by an unauthenticated caller), gated behind the same
// requireSupabaseAuth + assertAdmin pattern as listAuditRunsAdmin. Runs are
// persisted with is_public=false and a source tag so they never leak onto
// the public leaderboard/shareable links and stay filterable from the batch
// job that produced them (e.g. the daily cold-outreach cron in Pulse).
const InternalAuditBatchSchema = z.object({
  domains: z.array(z.string().trim().min(4).max(500)).min(1).max(50),
  locale: z.enum(["en", "es"]).default("en"),
  source: z.string().trim().min(1).max(100),
});

export interface InternalAuditBatchResultItem {
  url: string;
  runId: string | null;
  slug: string | null;
  overallScore: number | null;
  error: string | null;
  // Top non-pass conversionFindings (falls back to the top overall findings
  // when everything passes) and the run's PDF link, so a caller like Pulse's
  // day-7 audit job can write Customer.io merge-tag attributes straight from
  // this response without a second lookup.
  topFindings: { label: string; detail: string }[];
  pdfUrl: string | null;
}

async function computeBatchResults(
  domains: string[],
  locale: "en" | "es",
  source: string,
): Promise<InternalAuditBatchResultItem[]> {
  const results: InternalAuditBatchResultItem[] = [];
  for (const url of domains) {
    const runId = crypto.randomUUID();
    try {
      const result = await computeStoreAudit(url, locale);
      await persistAuditRun({
        runId,
        inputUrl: url,
        normalizedUrl: result.storeUrl,
        locale,
        storeScore: result.overallScore,
        storeResult: result as unknown as Record<string, unknown>,
        storeDataQuality: result.dataQuality,
        aeoScore: null,
        aeoResult: null,
        aeoDataQuality: null,
        isPublic: false,
        source,
        skipThrottle: true,
      });
      const slug = normalizeAuditHostname(result.storeUrl)?.slug ?? null;
      const nonPass = result.conversionFindings.filter((f) => f.status !== "pass");
      const topFindings = (nonPass.length > 0 ? nonPass : result.conversionFindings)
        .slice(0, 2)
        .map((f) => ({ label: f.label, detail: f.detail }));
      results.push({
        url,
        runId,
        slug,
        overallScore: result.overallScore,
        error: null,
        topFindings,
        pdfUrl: slug ? `https://shugert.com.mx/audit/results/${slug}/pdf` : null,
      });
    } catch (err) {
      results.push({
        url,
        runId: null,
        slug: null,
        overallScore: null,
        error: err instanceof Error ? err.message : String(err),
        topFindings: [],
        pdfUrl: null,
      });
    }
  }
  return results;
}

export const runInternalAuditBatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => InternalAuditBatchSchema.parse(input))
  .handler(async ({ data, context }): Promise<{ results: InternalAuditBatchResultItem[] }> => {
    await assertAdmin(context.userId);
    const results = await computeBatchResults(data.domains, data.locale, data.source);
    return { results };
  });

// ---------- Server function: internal batch audit via shared secret ----------
// Same computeBatchResults loop as runInternalAuditBatch above, but for a
// trusted server-to-server caller (Pulse's daily day-7 audit job) that has
// no Supabase session to present — auth is a shared secret header instead
// of requireSupabaseAuth/assertAdmin. Called from the stable HTTP route
// src/routes/api/public/internal/run-audit-batch.ts, never directly by a
// browser client, so it deliberately has no client-callable usage anywhere.
/**
 * Fixed SHA-256 verifier for the Pulse-only rotated shared secret. Only the
 * digest lives in source; the secret itself is never stored or logged here.
 */
const PULSE_INTERNAL_SECRET_SHA256 =
  "612675bdf58aea32d0e578ce3a531ea83b05e6ffe2bb40e974f940f60c135a2b";

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function constantTimeEquals(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export async function isAuthorizedInternalAuditSecret(
  secret: string | null | undefined,
  expected: string | undefined,
): Promise<boolean> {
  if (!secret) return false;
  // Existing path, unchanged.
  if (expected && secret === expected) return true;
  // Pulse-only fallback: compare SHA-256 digests in constant time.
  const digest = await sha256Hex(secret);
  return constantTimeEquals(digest, PULSE_INTERNAL_SECRET_SHA256);
}

export const runInternalAuditBatchViaSecret = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => InternalAuditBatchSchema.parse(input))
  .handler(async ({ data }): Promise<{ results: InternalAuditBatchResultItem[] }> => {
    const request = getRequest();
    const secret = request?.headers.get("x-internal-secret");
    if (!(await isAuthorizedInternalAuditSecret(secret, process.env.INTERNAL_PIPELINE_SECRET))) {
      throw new Response("Unauthorized", { status: 401 });
    }
    const results = await computeBatchResults(data.domains, data.locale, data.source);
    return { results };
  });

// ---------- Server function: capture lead ----------
const LeadSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  store_url: z
    .string()
    .trim()
    .min(4)
    .max(500)
    .url()
    .refine((v) => /^https?:\/\//i.test(v), "Must be an http(s) URL"),
  overall_score: z.number().int().min(0).max(100),
  checks: z
    .array(
      z.object({
        label: z.string().trim().min(1).max(120),
        status: z.enum(["pass", "warning", "fail"]),
      }),
    )
    .max(20),
  score_label: z.string().trim().min(1).max(60).optional(),
  top_fixes: z
    .array(
      z.object({
        title: z.string().trim().min(1).max(160),
        description: z.string().trim().min(1).max(600),
      }),
    )
    .max(3)
    .optional(),
  aeo: z
    .object({
      score: z.number().int().min(0).max(100),
      label: z.string().trim().min(1).max(60),
      llms_status: z.enum(["pass", "warning", "fail"]).nullable().optional(),
    })
    .nullable()
    .optional(),
  detected_platform: z
    .enum(["shopify", "woocommerce", "wix", "squarespace", "bigcommerce", "magento", "other"])
    .optional(),
  locale: z.enum(["en", "es"]).default("en"),
  form_started_at: z.number().int().positive(),
  website_confirmation: z.string().max(200).optional().or(z.literal("")),
  turnstile_token: z.string().max(4000).optional().or(z.literal("")),
});

export const submitAuditLead = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => LeadSchema.parse(input))
  .handler(async ({ data }) => {
    await assertPublicFormSecurity({
      form: "audit_lead",
      input: data,
      minimumMs: 1200,
      maxRequests: 4,
      windowMs: 30 * 60 * 1000,
    });
    const auditLeadTable = supabaseAdmin.from("contact_submissions") as any;
    const duplicateCutoff = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const { data: duplicate } = await auditLeadTable
      .select("id")
      .eq("email", data.email.toLowerCase())
      .gte("created_at", duplicateCutoff)
      .limit(1);
    if (duplicate?.length)
      throw new Error("This audit has already been unlocked recently. Please check your email.");
    const checksLine = data.checks.map((c) => `- ${c.label}: ${c.status.toUpperCase()}`).join("\n");
    const message = [
      `Source: Free Store Audit Tool`,
      `Store URL: ${data.store_url}`,
      `Overall score: ${data.overall_score}/100`,
      ``,
      `Checks:`,
      checksLine,
    ].join("\n");

    const { error } = await auditLeadTable.insert({
      full_name: data.name,
      email: data.email.toLowerCase(),
      message,
      locale: data.locale,
    });
    if (error) {
      console.error("submitAuditLead error", error);
      throw new Error("Failed to submit. Please try again.");
    }

    const leadEmail = data.email.toLowerCase();

    // Delivery side-effects never fail the user submission, but every outcome
    // (success or failure) is persisted to `lead_delivery_log` so completions
    // can be audited long after the 1-hour worker log window expires.
    try {
      await sendAuditLeadEmails(data, checksLine);
      await logLeadDelivery({ email: leadEmail, channel: "resend_audit_email", status: "ok" });
    } catch (e) {
      console.error("submitAuditLead email error", e);
      await logLeadDelivery({
        email: leadEmail,
        channel: "resend_audit_email",
        status: "failed",
        httpStatus: httpStatusFromError(e),
        detail: errorDetail(e),
      });
    }

    // Add lead to Resend audience for the lead-magnet automation.
    try {
      await addLeadToResendAudience(data);
      await logLeadDelivery({ email: leadEmail, channel: "resend_audience", status: "ok" });
    } catch (e) {
      console.error("submitAuditLead audience error", e);
      await logLeadDelivery({
        email: leadEmail,
        channel: "resend_audience",
        status: "failed",
        httpStatus: httpStatusFromError(e),
        detail: errorDetail(e),
      });
    }

    // Sync lead + audit_completed event to Customer.io to drive the
    // nurture journey (correo 1 keeps going out via Resend above).
    try {
      const result = await syncLeadToCustomerIo(data);
      await logLeadDelivery({
        email: leadEmail,
        channel: "customerio_audit_completed",
        status: result.ok ? "ok" : "failed",
        attempts: result.attempts,
        httpStatus: result.status || null,
        detail: `${result.via}: ${result.detail}`.slice(0, 2000),
        payload: result.payload,
      });
    } catch (e) {
      console.error("submitAuditLead customerio error", e);
      await logLeadDelivery({
        email: leadEmail,
        channel: "customerio_audit_completed",
        status: "failed",
        httpStatus: httpStatusFromError(e),
        detail: errorDetail(e),
      });
    }

    return { ok: true };
  });

// ---------- Delivery observability ----------
function errorDetail(e: unknown): string {
  return (e instanceof Error ? e.message : String(e)).slice(0, 2000);
}

function httpStatusFromError(e: unknown): number | null {
  const match = errorDetail(e).match(/\[(\d{3})\]/);
  return match ? Number(match[1]) : null;
}

/**
 * Persist the outcome of one delivery channel for one lead. Best-effort:
 * a logging failure must never surface to the user or mask the real error.
 */
async function logLeadDelivery(entry: {
  email: string;
  channel: string;
  status: "ok" | "failed";
  attempts?: number;
  httpStatus?: number | null;
  detail?: string | null;
  payload?: unknown;
}) {
  try {
    const table = supabaseAdmin.from("lead_delivery_log") as any;
    const { error } = await table.insert({
      email: entry.email,
      source: "free_audit",
      channel: entry.channel,
      status: entry.status,
      attempts: entry.attempts ?? 1,
      http_status: entry.httpStatus ?? null,
      detail: entry.detail ?? null,
      payload: entry.payload ?? null,
    });
    if (error) console.error("logLeadDelivery insert error", error);
  } catch (e) {
    console.error("logLeadDelivery error", e);
  }
}

// ---------- Email delivery via Resend gateway ----------
const RESEND_GATEWAY = "https://connector-gateway.lovable.dev/resend";
const FROM_ADDRESS = "Shugert <noreply@app.shugert.com.mx>";
const REPLY_TO = "hello@shugert.com.mx";
const NOTIFY_TO = "hello@shugert.com.mx";

async function sendResendEmail(payload: {
  to: string | string[];
  subject: string;
  html: string;
  reply_to?: string;
}) {
  const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
  const RESEND_API_KEY = process.env.RESEND_API_KEY;
  if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY is not configured");
  if (!RESEND_API_KEY) throw new Error("RESEND_API_KEY is not configured");

  const res = await fetch(`${RESEND_GATEWAY}/emails`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${LOVABLE_API_KEY}`,
      "X-Connection-Api-Key": RESEND_API_KEY,
    },
    body: JSON.stringify({ from: FROM_ADDRESS, ...payload }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Resend send failed [${res.status}]: ${body}`);
  }
  return res.json();
}

async function sendAuditLeadEmails(data: z.infer<typeof LeadSchema>, checksLine: string) {
  const pass = data.checks.filter((c) => c.status === "pass").length;
  const warn = data.checks.filter((c) => c.status === "warning").length;
  const fail = data.checks.filter((c) => c.status === "fail").length;
  const isEs = data.locale === "es";

  // 1) Confirmation to the lead
  const leadSubject = isEs
    ? `Tu tienda Shopify obtuvo ${data.overall_score}/100, esto es lo primero a arreglar`
    : `Your Shopify store scored ${data.overall_score}/100, here's what to fix first`;
  const leadHtml = renderLeadEmail({ data, pass, warn, fail, isEs });

  // 2) Internal notification
  const adminSubject = `New audit lead: ${data.email} (${data.overall_score}/100)`;
  const adminHtml = renderAdminEmail({ data, pass, warn, fail, checksLine });

  await Promise.all([
    sendResendEmail({ to: data.email, subject: leadSubject, html: leadHtml, reply_to: REPLY_TO }),
    sendResendEmail({
      to: NOTIFY_TO,
      subject: adminSubject,
      html: adminHtml,
      reply_to: data.email,
    }),
  ]);
}

// ---------- Resend audience signup (lead magnet automation) ----------
const AUDIENCE_NAME = "Free Store Audit Leads";
let cachedAudienceId: string | null = null;

async function resendFetch(path: string, init?: RequestInit) {
  const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
  const RESEND_API_KEY = process.env.RESEND_API_KEY;
  if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY is not configured");
  if (!RESEND_API_KEY) throw new Error("RESEND_API_KEY is not configured");
  const res = await fetch(`${RESEND_GATEWAY}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${LOVABLE_API_KEY}`,
      "X-Connection-Api-Key": RESEND_API_KEY,
      ...(init?.headers ?? {}),
    },
  });
  const body = await res.text();
  if (!res.ok) {
    throw new Error(`Resend ${path} failed [${res.status}]: ${body}`);
  }
  return body ? JSON.parse(body) : {};
}

async function getOrCreateAuditAudienceId(): Promise<string> {
  if (cachedAudienceId) return cachedAudienceId;
  // Try list first
  const list = await resendFetch("/audiences", { method: "GET" }).catch(() => null);
  const existing = list?.data?.find?.(
    (a: { id: string; name: string }) => a.name === AUDIENCE_NAME,
  );
  if (existing?.id) {
    cachedAudienceId = existing.id;
    return existing.id;
  }
  const created = await resendFetch("/audiences", {
    method: "POST",
    body: JSON.stringify({ name: AUDIENCE_NAME }),
  });
  cachedAudienceId = created.id;
  return created.id;
}

async function addLeadToResendAudience(data: z.infer<typeof LeadSchema>) {
  const audienceId = await getOrCreateAuditAudienceId();
  const [firstName, ...rest] = data.name.trim().split(/\s+/);
  await resendFetch(`/audiences/${audienceId}/contacts`, {
    method: "POST",
    body: JSON.stringify({
      email: data.email,
      first_name: firstName ?? data.name,
      last_name: rest.join(" ") || undefined,
      unsubscribed: false,
    }),
  });
}

// ---------- Customer.io sync (immediate Resend + 3-email nurture journey) ----------
const CUSTOMERIO_TRACK_URL = "https://track.customer.io/api/v1";
const CUSTOMERIO_TRACK_V2_URL = "https://track.customer.io/api/v2";

function customerIoAuthHeader() {
  const CUSTOMERIO_SITE_ID = process.env.CUSTOMERIO_SITE_ID;
  const CUSTOMERIO_API_KEY = process.env.CUSTOMERIO_API_KEY;
  if (!CUSTOMERIO_SITE_ID) throw new Error("CUSTOMERIO_SITE_ID is not configured");
  if (!CUSTOMERIO_API_KEY) throw new Error("CUSTOMERIO_API_KEY is not configured");
  return `Basic ${Buffer.from(`${CUSTOMERIO_SITE_ID}:${CUSTOMERIO_API_KEY}`).toString("base64")}`;
}

/**
 * Single POST/PUT to the Customer.io Track API with one retry on transient
 * failures (429 / 5xx / network). Always logs the outcome so a silently
 * dropped call is visible in server logs instead of disappearing.
 */
async function customerIoRequest(
  url: string,
  method: "PUT" | "POST",
  body: unknown,
  label: string,
) {
  const auth = customerIoAuthHeader();
  let lastError = "";
  let lastStatus = 0;
  let used = 0;

  for (let attempt = 1; attempt <= 2; attempt++) {
    used = attempt;
    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: auth },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      lastStatus = res.status;
      if (res.ok) {
        console.info(
          `customerio ${label} ok [${res.status}] attempt=${attempt} ${text.slice(0, 200)}`,
        );
        return { ok: true as const, status: res.status, body: text, attempts: attempt };
      }
      lastError = `[${res.status}] ${text.slice(0, 300)}`;
      // 4xx (other than 429) will not succeed on retry.
      if (res.status !== 429 && res.status < 500) break;
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
    }
  }

  console.error(`customerio ${label} failed attempts=${used} ${lastError}`);
  return { ok: false as const, status: lastStatus, body: lastError, attempts: used };
}

type AuditCommercialRoute = {
  id:
    | "performance_fix"
    | "cro_technical_seo_fix"
    | "shopify_migration"
    | "geo_readiness"
    | "custom_build_replatform"
    | "no_immediate_offer";
  path: string;
  geoRecommended: boolean;
  label: { en: string; es: string };
  body: { en: string; es: string };
};

function getAuditCommercialRoute(data: z.infer<typeof LeadSchema>): AuditCommercialRoute {
  const platform = data.detected_platform ?? "shopify";
  const failText = data.checks
    .filter((check) => check.status === "fail")
    .map((check) => check.label)
    .join(" ")
    .toLowerCase();
  const aeoNeedsHelp = Boolean(data.aeo && data.aeo.score < 60);
  const isMigratable = ["woocommerce", "bigcommerce", "magento"].includes(platform);

  if (isMigratable) {
    return {
      id: "shopify_migration",
      path: data.locale === "es" ? "/es/migracion-shopify" : "/shopify-migration",
      geoRecommended: false,
      label: { en: "Shopify Migration", es: "Migración a Shopify" },
      body: {
        en: "Your current platform makes migration the relevant commercial path. Shugert can scope data, redirects, SEO continuity, integrations, QA, cutover and stabilization for standard Shopify or Shopify Plus according to the requirements.",
        es: "Tu plataforma actual hace que migración sea la ruta comercial relevante. Shugert puede definir datos, redirects, continuidad SEO, integraciones, QA, cutover y estabilización para Shopify estándar o Shopify Plus según los requisitos.",
      },
    };
  }

  if (platform !== "shopify") {
    if (aeoNeedsHelp) {
      return {
        id: "geo_readiness",
        path: "/geo-readiness",
        geoRecommended: true,
        label: { en: "GEO Readiness Audit", es: "GEO Readiness Audit" },
        body: {
          en: "The strongest relevant next step is a paid GEO diagnosis of citation surfaces, entity evidence, technical access and the prioritized AI-visibility roadmap.",
          es: "El siguiente paso relevante es un diagnóstico GEO pagado de superficies de cita, evidencia de entidad, acceso técnico y roadmap priorizado de visibilidad en IA.",
        },
      };
    }
    return {
      id: "no_immediate_offer",
      path: "",
      geoRecommended: false,
      label: { en: "No immediate paid recommendation", es: "Sin recomendación pagada inmediata" },
      body: {
        en: "No paid next step is being forced from this scan.",
        es: "Este scan no fuerza un siguiente paso pagado.",
      },
    };
  }

  const performanceFailure =
    /mobile performance|desktop performance|largest contentful paint|render-blocking|image optimization|rendimiento en móvil|rendimiento en desktop|recursos que bloquean|optimización de imágenes/.test(
      failText,
    );
  const croSeoFailure =
    /seo|accessibility|accesibilidad|organic visibility|visibilidad orgánica/.test(failText);

  if (performanceFailure) {
    return {
      id: "performance_fix",
      path: data.locale === "es" ? "/es/rendimiento" : "/shopify-performance",
      geoRecommended: false,
      label: { en: "Performance Fix", es: "Performance Fix" },
      body: {
        en: "The strongest next step is a bounded Performance Fix with a comparable baseline, root-cause map, staged implementation and before/after validation.",
        es: "El siguiente paso más sólido es un Performance Fix acotado con baseline comparable, mapa de causa raíz, implementación staged y validación antes/después.",
      },
    };
  }

  if (croSeoFailure) {
    return {
      id: "cro_technical_seo_fix",
      path: data.locale === "es" ? "/es/cro-shopify" : "/shopify-cro",
      geoRecommended: false,
      label: { en: "CRO & Technical SEO Fix", es: "CRO & Technical SEO Fix" },
      body: {
        en: "The strongest next step is a bounded CRO & Technical SEO Fix covering the measurable buying, crawl or indexation constraint.",
        es: "El siguiente paso más sólido es un CRO & Technical SEO Fix acotado para la restricción medible de compra, crawling o indexación.",
      },
    };
  }

  if (aeoNeedsHelp) {
    return {
      id: "geo_readiness",
      path: "/geo-readiness",
      geoRecommended: true,
      label: { en: "GEO Readiness Audit", es: "GEO Readiness Audit" },
      body: {
        en: "Storefront fundamentals are not the dominant failure, but AI readability is weak. A paid GEO diagnosis is the relevant next decision.",
        es: "Los fundamentos del storefront no son el fallo dominante, pero la legibilidad por IA es baja. Un diagnóstico GEO pagado es la siguiente decisión relevante.",
      },
    };
  }

  return {
    id: "custom_build_replatform",
    path: data.locale === "es" ? "/es/desarrollo-shopify" : "/shopify-experts",
    geoRecommended: false,
    label: { en: "Custom Build or Replatform", es: "Build Custom o Replatform" },
    body: {
      en: "The scan does not show a dominant performance, CRO, SEO or GEO failure. A scoped technical review is more responsible than forcing a generic fix.",
      es: "El scan no muestra un fallo dominante de performance, CRO, SEO o GEO. Una revisión técnica acotada es más responsable que forzar un fix genérico.",
    },
  };
}

async function syncLeadToCustomerIo(data: z.infer<typeof LeadSchema>) {
  const failingChecks = data.checks.filter((check) => check.status === "fail");
  const topFail1 = failingChecks[0]?.label ?? null;
  const topFail2 = failingChecks[1]?.label ?? null;
  const firstName = data.name.trim().split(/\s+/)[0] ?? data.name;
  const recommendation = getAuditCommercialRoute(data);
  const email = data.email.trim().toLowerCase();
  const nowSeconds = Math.floor(Date.now() / 1000);

  const funnelAttributes = {
    audit_score: data.overall_score,
    top_fail_1: topFail1,
    top_fail_2: topFail2,
    store_url: data.store_url,
    locale: data.locale,
    detected_platform: data.detected_platform ?? "shopify",
    aeo_score: data.aeo?.score ?? null,
    funnel_stage: "free_audit",
    recommended_offer: recommendation.id,
    recommended_path: recommendation.path,
    geo_recommended: recommendation.geoRecommended,
    retainer_eligible: false,
    nurture_version: "audit-v2",
  };

  const profileAttributes = {
    email,
    brand: "shugert",
    first_name: firstName,
    ...funnelAttributes,
    audit_completed_at: nowSeconds,
  };

  const eventAttributes = {
    ...funnelAttributes,
    // Kept for backwards compatibility with existing campaign liquid.
    score: data.overall_score,
  };

  // Preferred path: one atomic Track v2 batch containing the identify AND the
  // audit_completed event, both keyed by an explicit email identifier. This
  // removes the ordering/identity ambiguity of the two separate v1 calls, where
  // the profile update landed but the campaign trigger event never registered.
  const batch = await customerIoRequest(
    `${CUSTOMERIO_TRACK_V2_URL}/batch`,
    "POST",
    {
      batch: [
        {
          type: "person",
          action: "identify",
          identifiers: { email },
          attributes: profileAttributes,
        },
        {
          type: "person",
          action: "event",
          identifiers: { email },
          name: "audit_completed",
          timestamp: nowSeconds,
          attributes: eventAttributes,
        },
      ],
    },
    "v2 batch identify+audit_completed",
  );

  const logPayload = {
    event: "audit_completed",
    email,
    recommended_offer: recommendation.id,
    audit_score: data.overall_score,
    aeo_score: data.aeo?.score ?? null,
  };

  if (batch.ok) {
    return {
      ok: true as const,
      via: "v2_batch",
      status: batch.status,
      attempts: batch.attempts,
      detail: batch.body.slice(0, 500),
      payload: logPayload,
    };
  }

  // Fallback: legacy v1 pair. The event is emitted even when the identify leg
  // fails, so a profile-update problem can never suppress the campaign trigger.
  const identify = await customerIoRequest(
    `${CUSTOMERIO_TRACK_URL}/customers/${encodeURIComponent(email)}`,
    "PUT",
    profileAttributes,
    "v1 identify fallback",
  );

  const event = await customerIoRequest(
    `${CUSTOMERIO_TRACK_URL}/customers/${encodeURIComponent(email)}/events`,
    "POST",
    { name: "audit_completed", data: eventAttributes },
    "v1 event fallback",
  );

  return {
    ok: event.ok,
    via: "v1_fallback",
    status: event.status,
    attempts: batch.attempts + identify.attempts + event.attempts,
    detail: `batch=${batch.body.slice(0, 200)} | identify=${identify.ok ? "ok" : identify.body.slice(0, 200)} | event=${event.ok ? "ok" : event.body.slice(0, 200)}`,
    payload: logPayload,
  };
}

function esc(s: string) {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}

const PLATFORM_LABELS: Record<DetectedPlatform, { en: string; es: string }> = {
  shopify: { en: "Shopify", es: "Shopify" },
  woocommerce: { en: "WooCommerce", es: "WooCommerce" },
  wix: { en: "Wix", es: "Wix" },
  squarespace: { en: "Squarespace", es: "Squarespace" },
  bigcommerce: { en: "BigCommerce", es: "BigCommerce" },
  magento: { en: "Magento", es: "Magento" },
  other: { en: "another platform", es: "otra plataforma" },
};

function renderLeadEmail({
  data,
  pass,
  warn,
  fail,
  isEs,
}: {
  data: z.infer<typeof LeadSchema>;
  pass: number;
  warn: number;
  fail: number;
  isEs: boolean;
}) {
  void pass;
  void warn;
  void fail;

  const failingChecks = data.checks.filter((c) => c.status === "fail");
  const onlyRbr =
    failingChecks.length === 1 &&
    failingChecks[0].label ===
      (isEs ? "Recursos que bloquean el renderizado" : "Render-Blocking Resources");

  const ctaBodyText = onlyRbr
    ? isEs
      ? "Los recursos que bloquean el renderizado son un problema común que resolvemos mediante Performance Fix. El rango público es $2,500–$4,000 USD e incluye baseline, implementación staged y validación antes/después."
      : "Render-blocking resources are a common problem addressed through Performance Fix. The public range is $2,500–$4,000 USD and includes a baseline, staged implementation and before/after validation."
    : isEs
      ? "Los problemas de performance como recursos que bloquean el renderizado, imágenes sin optimizar y carga móvil lenta se resuelven mediante Performance Fix. El rango público es $2,500–$4,000 USD con baseline e implementación validada."
      : "Performance issues such as render-blocking resources, unoptimized images and slow mobile loading are addressed through Performance Fix. The public range is $2,500–$4,000 USD with a baseline and validated implementation.";

  const t = isEs
    ? {
        hi: (n: string) => `Hola ${n},`,
        intro: (url: string) =>
          `Tu auditoría gratuita de Shopify para <a href="${url}" style="color:#0c0a09;">${url}</a> está lista.`,
        scoreLine: (s: number, label: string) =>
          `Puntuación general: <strong>${s}/100</strong>, ${label}`,
        topTitle: "Estos son tus 3 fixes de mayor impacto:",
        fullTitle: "Resultados completos de tu auditoría:",
        aeoLabel: "Legibilidad por IA (AEO)",
        llmsLabel: "Archivo llms.txt",
        llmsPass: "presente y bien estructurado",
        llmsWarn: "existe pero su estructura puede mejorar",
        llmsFail: "no se encontró",
        ctaHeader: "¿Quieres que lo arreglemos por ti?",
        ctaBody: ctaBodyText,
        ctaButton: "Ver el plan de fixes y precios →",
        replyLine: "O simplemente responde a este correo. Leemos todo.",
        signName: "Samuel Noriega",
        signTitle: "Founder & CEO, Shugert",
        signMeta: "Shopify Select Partner desde 2015",
      }
    : {
        hi: (n: string) => `Hi ${n},`,
        intro: (url: string) =>
          `Your free Shopify store audit for <a href="${url}" style="color:#0c0a09;">${url}</a> is ready.`,
        scoreLine: (s: number, label: string) =>
          `Overall score: <strong>${s}/100</strong>, ${label}`,
        topTitle: "Here are your 3 highest-impact fixes:",
        fullTitle: "Your full audit results:",
        aeoLabel: "AI Readability (AEO)",
        llmsLabel: "llms.txt file",
        llmsPass: "present and well structured",
        llmsWarn: "exists but structure needs improvement",
        llmsFail: "not found",
        ctaHeader: "Want us to fix these for you?",
        ctaBody: ctaBodyText,
        ctaButton: "See the fix plan and pricing →",
        replyLine: "Or reply to this email directly. We read everything.",
        signName: "Samuel Noriega",
        signTitle: "Founder & CEO, Shugert",
        signMeta: "Shopify Select Partner since 2015",
      };

  const firstName = (data.name.trim().split(/\s+/)[0] || data.name).trim();
  const platform = data.detected_platform ?? "shopify";
  const isNonShopify = platform !== "shopify";
  const platformLabel = PLATFORM_LABELS[platform][isEs ? "es" : "en"];
  const recommendation = getAuditCommercialRoute(data);

  const introHtml = isNonShopify
    ? isEs
      ? `Tu auditoría gratuita para <a href="${esc(data.store_url)}" style="color:#0c0a09;">${esc(data.store_url)}</a> está lista. Notamos que no corre en Shopify, parece ser ${esc(platformLabel)}. Igual corrimos el resto de las variables (performance, SEO, legibilidad por IA) porque esos checks aplican a cualquier plataforma.`
      : `Your free audit for <a href="${esc(data.store_url)}" style="color:#0c0a09;">${esc(data.store_url)}</a> is ready. We noticed it's not running on Shopify, looks like ${esc(platformLabel)}. We still ran the rest of the checks (performance, SEO, AI readability) since those apply to any platform.`
    : t.intro(esc(data.store_url));

  const cta =
    recommendation.id === "no_immediate_offer"
      ? { show: false, header: "", body: "", buttonLabel: "", buttonHref: "" }
      : {
          show: true,
          header: isEs
            ? `Siguiente paso recomendado: ${recommendation.label.es}`
            : `Recommended next step: ${recommendation.label.en}`,
          body: isEs ? recommendation.body.es : recommendation.body.en,
          buttonLabel: isEs
            ? `Revisar ${recommendation.label.es} →`
            : `Review ${recommendation.label.en} →`,
          buttonHref: `https://shugert.com.mx${recommendation.path}?utm_source=audit-email&utm_medium=email&utm_campaign=audit-v2&utm_content=${recommendation.id}&store_url=${encodeURIComponent(data.store_url)}&score=${data.overall_score}`,
        };

  const scoreColor =
    data.overall_score >= 80 ? "#16a34a" : data.overall_score >= 50 ? "#d97706" : "#dc2626";
  const scoreLabel =
    data.score_label ||
    (data.overall_score >= 80
      ? isEs
        ? "Tienda sólida"
        : "Strong Store"
      : data.overall_score >= 50
        ? isEs
          ? "Por mejorar"
          : "Room to Improve"
        : isEs
          ? "Requiere atención"
          : "Needs Attention");

  const statusIcon = (s: "pass" | "warning" | "fail") =>
    s === "pass" ? "✅" : s === "warning" ? "⚠️" : "❌";

  const topFixesHtml = (data.top_fixes && data.top_fixes.length > 0 ? data.top_fixes : [])
    .map(
      (f, i) => `
        <tr><td style="padding:0 0 18px;">
          <p style="margin:0 0 4px;font-size:15px;font-weight:600;color:#0c0a09;">${i + 1}. ${esc(f.title)}</p>
          <p style="margin:0;font-size:14px;line-height:1.6;color:#44403c;">${esc(f.description)}</p>
        </td></tr>`,
    )
    .join("");

  const summaryRows = (data.checks || [])
    .map(
      (c) => `
        <tr><td style="padding:6px 0;font-size:14px;color:#44403c;border-bottom:1px solid #f5f5f4;">
          <span style="display:inline-block;width:22px;">${statusIcon(c.status)}</span>
          ${esc(c.label)}
        </td></tr>`,
    )
    .join("");

  const aeoRowHtml = data.aeo
    ? `<tr><td style="padding:6px 0;font-size:14px;color:#44403c;border-bottom:1px solid #f5f5f4;">
          <span style="display:inline-block;width:22px;">${data.aeo.score >= 80 ? "✅" : data.aeo.score >= 50 ? "⚠️" : "❌"}</span>
          ${t.aeoLabel}: <strong>${data.aeo.score}/100</strong>, ${esc(data.aeo.label)}
        </td></tr>`
    : "";

  const llmsRowHtml = data.aeo?.llms_status
    ? `<tr><td style="padding:6px 0;font-size:14px;color:#44403c;">
          <span style="display:inline-block;width:22px;">${statusIcon(data.aeo.llms_status)}</span>
          ${t.llmsLabel}: ${data.aeo.llms_status === "pass" ? t.llmsPass : data.aeo.llms_status === "warning" ? t.llmsWarn : t.llmsFail}
        </td></tr>`
    : "";

  return `<!doctype html>
<html><body style="margin:0;padding:0;background:#ffffff;font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:#111;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#ffffff;padding:32px 16px;">
    <tr><td align="center">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;">
        <tr><td style="padding:0 8px 24px;">
          <p style="margin:0;font-size:13px;letter-spacing:.14em;text-transform:uppercase;color:#78716c;">Shugert</p>
        </td></tr>

        <tr><td style="padding:0 8px;">
          <p style="margin:0 0 16px;font-size:16px;line-height:1.6;color:#0c0a09;">${t.hi(esc(firstName))}</p>
          <p style="margin:0 0 24px;font-size:15px;line-height:1.7;color:#44403c;">${introHtml}</p>

          <p style="margin:0 0 28px;font-size:16px;line-height:1.6;color:#0c0a09;border-left:3px solid ${scoreColor};padding:8px 0 8px 14px;">
            ${t.scoreLine(data.overall_score, esc(scoreLabel))}
          </p>
        </td></tr>

        ${
          topFixesHtml
            ? `
        <tr><td style="padding:8px 8px 4px;">
          <p style="margin:0 0 14px;font-size:15px;font-weight:600;color:#0c0a09;">${t.topTitle}</p>
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">${topFixesHtml}</table>
        </td></tr>`
            : ""
        }

        <tr><td style="padding:18px 8px 4px;">
          <p style="margin:0 0 10px;font-size:15px;font-weight:600;color:#0c0a09;">${t.fullTitle}</p>
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            ${summaryRows}
            ${aeoRowHtml}
            ${llmsRowHtml}
          </table>
        </td></tr>

        <tr><td style="padding:32px 8px 8px;">
          ${
            cta.show
              ? `<h2 style="margin:0 0 10px;font-size:18px;color:#0c0a09;">${cta.header}</h2>
          <p style="margin:0 0 20px;font-size:15px;line-height:1.7;color:#44403c;">${cta.body}</p>
          <p style="margin:0 0 20px;">
            <a href="${cta.buttonHref}" style="display:inline-block;background:#0c0a09;color:#ffffff;text-decoration:none;font-size:15px;font-weight:500;padding:14px 24px;border-radius:999px;">
              ${cta.buttonLabel}
            </a>
          </p>`
              : ""
          }
          <p style="margin:0 0 32px;font-size:14px;line-height:1.6;color:#57534e;">${t.replyLine}</p>
        </td></tr>

        <tr><td style="padding:8px 8px 24px;border-top:1px solid #e7e5e4;">
          <p style="margin:16px 0 2px;font-size:14px;font-weight:600;color:#0c0a09;">${t.signName}</p>
          <p style="margin:0 0 2px;font-size:13px;color:#57534e;">${t.signTitle}</p>
          <p style="margin:0;font-size:12px;color:#a8a29e;">${t.signMeta}</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`;
}

function renderAdminEmail({
  data,
  pass,
  warn,
  fail,
  checksLine,
}: {
  data: z.infer<typeof LeadSchema>;
  pass: number;
  warn: number;
  fail: number;
  checksLine: string;
}) {
  return `<!doctype html>
<html><body style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:#111;max-width:560px;margin:0 auto;padding:24px;">
  <h2 style="margin:0 0 16px;">New audit lead 🔔</h2>
  <table cellpadding="6" cellspacing="0" style="font-size:14px;border-collapse:collapse;">
    <tr><td style="color:#78716c;">Name</td><td><strong>${esc(data.name)}</strong></td></tr>
    <tr><td style="color:#78716c;">Email</td><td><a href="mailto:${esc(data.email)}">${esc(data.email)}</a></td></tr>
    <tr><td style="color:#78716c;">Store</td><td><a href="${esc(data.store_url)}">${esc(data.store_url)}</a></td></tr>
    <tr><td style="color:#78716c;">Score</td><td><strong>${data.overall_score}/100</strong></td></tr>
    <tr><td style="color:#78716c;">Checks</td><td>${pass} pass · ${warn} warning · ${fail} fail</td></tr>
    <tr><td style="color:#78716c;">Platform</td><td>${esc(data.detected_platform ?? "shopify")}</td></tr>
    <tr><td style="color:#78716c;">Locale</td><td>${data.locale}</td></tr>
  </table>
  <pre style="margin-top:16px;background:#fafaf9;border:1px solid #e7e5e4;border-radius:8px;padding:12px;font-size:12px;white-space:pre-wrap;">${esc(checksLine)}</pre>
  <p style="margin-top:16px;font-size:12px;color:#78716c;">View all leads: <a href="https://shugert.com.mx/admin/leads">admin/leads</a></p>
</body></html>`;
}

// ============================================================================
// AEO (Answer Engine Optimization) audit
// Checks whether ChatGPT / Claude / Perplexity / Google AI can access,
// understand, and cite the site. Modeled on the xipe AEOAudit report.
// ============================================================================

export type AeoGroupId = "crawler" | "rendering" | "structure" | "authority" | "parseability";

export interface AeoCheck {
  id: string;
  groupId: AeoGroupId;
  label: string;
  status: CheckStatus;
  score: number; // 0-100
  message: string;
  fix?: string;
}

export interface AeoGroup {
  id: AeoGroupId;
  label: string;
}

export interface AeoResult {
  url: string;
  overallScore: number;
  scoreLabel: "good" | "ok" | "bad";
  groups: AeoGroup[];
  checks: AeoCheck[];
  fetchedMs: number;
  note?: string;
}

type AeoCopy = {
  groups: Record<AeoGroupId, string>;
  labels: Record<string, string>;
  pass: Record<string, string>;
  warn: Record<string, string>;
  fail: Record<string, string>;
  fix: Record<string, string>;
  errors: { unreachable: string };
};

const AEO_EN: AeoCopy = {
  groups: {
    crawler: "AI Crawler Access",
    rendering: "Content Rendering",
    structure: "Structure & Semantics",
    authority: "Authority & Depth",
    parseability: "Parseability",
  },
  labels: {
    aiCrawlers: "AI crawler access",
    https: "HTTPS, status & redirects",
    llmsTxt: "llms.txt file",
    sitemap: "Sitemap XML",
    noJs: "Content available without JavaScript",
    schema: "Structured data (Schema.org)",
    headings: "Heading hierarchy",
    meta: "Metadata & Open Graph",
    semantic: "Semantic HTML",
    depth: "Depth & authorship signals",
    parse: "Parseability (alt & link text)",
  },
  pass: {
    aiCrawlers: "No known AI crawler is blocked in robots.txt.",
    https: "HTTPS OK, status 200, clean redirects.",
    llmsTxt: "Found /llms.txt: content guidance for AI agents.",
    sitemap: "Sitemap found.",
    noJs: "Server HTML renders meaningful content without JavaScript.",
    schema: "JSON-LD structured data present.",
    headings: "Heading structure looks clean (1 H1, properly nested).",
    meta: "Title, description, canonical, lang and Open Graph all present.",
    semantic: "Semantic HTML tags present (main, article, section, nav, header, footer).",
    depth: "Solid depth and authorship signals (word count, author, date).",
    parse: "Images have alt text and links use descriptive labels.",
  },
  warn: {
    aiCrawlers:
      "Some AI crawlers are blocked. This limits which answer engines can read your site.",
    https: "HTTPS works but the redirect chain or status code could be cleaner.",
    llmsTxt: "/llms.txt exists but the structure looks thin.",
    sitemap: "Sitemap found but not declared in robots.txt, declare it for better discovery.",
    noJs: "Server HTML is light on visible text. Some content likely requires JavaScript.",
    schema:
      "Only minimal JSON-LD found. Add Organization/WebSite plus Article/FAQ/Product where relevant.",
    headings: "Heading hierarchy has minor issues (skipped levels or duplicate H1s).",
    meta: "Some metadata is missing. Title/description/canonical/OG should all be present.",
    semantic:
      "A few semantic tags are missing. Aim for main, article, section, nav, header, footer.",
    depth: "Depth is OK but signals are thin, add an FAQ section and a published date.",
    parse: "Some images lack alt text or some links use vague phrases like 'click here'.",
  },
  fail: {
    aiCrawlers: "AI crawlers are blocked in robots.txt, answer engines cannot read this site.",
    https: "HTTPS or status check failed. Page is not reachable cleanly over HTTPS.",
    llmsTxt: "No /llms.txt file. LLMs have no curated guide to your most important content.",
    sitemap: "No sitemap found. Crawlers cannot discover your URLs efficiently.",
    noJs: "Server HTML has almost no visible text. Content is JS-only and invisible to most AI crawlers.",
    schema: "No JSON-LD. Structured data helps LLMs extract facts unambiguously.",
    headings: "Heading structure is broken (no H1 or multiple H1s).",
    meta: "Critical metadata missing (title or description). Open Graph absent.",
    semantic: "No semantic HTML tags found. AI parsers cannot distinguish layout from content.",
    depth: "Content is thin, LLMs need depth, an author, and a date to cite a page.",
    parse: "Most images have no alt text and link text is vague, hard for AI to parse.",
  },
  fix: {
    aiCrawlers:
      "Update robots.txt to allow GPTBot, ChatGPT-User, Google-Extended, ClaudeBot, PerplexityBot and CCBot.",
    https: "Serve every URL over HTTPS, return 200, and avoid multi-step redirects.",
    llmsTxt:
      "Add a /llms.txt file: an H1 with your site name, a one-line summary, and grouped Markdown links to your key pages.",
    sitemap: "Generate /sitemap.xml and reference it in robots.txt with a Sitemap: line.",
    noJs: "Server-render or pre-render your most important content so it shows up without JavaScript.",
    schema:
      "Add JSON-LD Schema.org: at minimum Organization/WebSite, plus Article, FAQPage, or Product where relevant.",
    headings: "Use exactly one H1 per page and nest H2/H3 in order without skipping levels.",
    meta: "Add a unique <title>, meta description, canonical link, html[lang], and Open Graph tags.",
    semantic: "Use semantic landmarks: <main>, <article>, <section>, <nav>, <header>, <footer>.",
    depth:
      "Add an FAQ section (ideally with FAQPage schema) and mark publish/update dates with <time datetime>.",
    parse: "Describe images with alt text and replace vague link labels with descriptive phrases.",
  },
  errors: { unreachable: "We couldn't reach that URL. Check the address and try again." },
};

const AEO_ES: AeoCopy = {
  groups: {
    crawler: "Acceso de crawlers de IA",
    rendering: "Renderizado de contenido",
    structure: "Estructura y semántica",
    authority: "Autoridad y profundidad",
    parseability: "Parseabilidad",
  },
  labels: {
    aiCrawlers: "Acceso de crawlers de IA",
    https: "HTTPS, estado y redirecciones",
    llmsTxt: "Archivo llms.txt",
    sitemap: "Sitemap XML",
    noJs: "Contenido disponible sin JavaScript",
    schema: "Datos estructurados (Schema.org)",
    headings: "Jerarquía de encabezados",
    meta: "Metadatos y Open Graph",
    semantic: "HTML semántico",
    depth: "Profundidad y señales de autoría",
    parse: "Parseabilidad (alt y enlaces)",
  },
  pass: {
    aiCrawlers: "Ningún crawler de IA conocido está bloqueado en robots.txt.",
    https: "HTTPS correcto, estado 200, redirecciones limpias.",
    llmsTxt: "Se encontró /llms.txt: guías de contenido para agentes de IA.",
    sitemap: "Sitemap encontrado.",
    noJs: "El HTML del servidor renderiza contenido significativo sin JavaScript.",
    schema: "Datos estructurados JSON-LD presentes.",
    headings: "Estructura correcta: 1 H1 y encabezados bien anidados.",
    meta: "Title, description, canonical, lang y Open Graph presentes.",
    semantic: "Etiquetas semánticas presentes: main, article, section, nav, header, footer.",
    depth: "Buenas señales de profundidad y autoría (palabras, autor, fecha).",
    parse: "Las imágenes tienen alt y los enlaces usan texto descriptivo.",
  },
  warn: {
    aiCrawlers:
      "Algunos crawlers de IA están bloqueados. Esto limita qué answer engines pueden leerte.",
    https: "HTTPS funciona pero la cadena de redirecciones o el estado podrían ser más limpios.",
    llmsTxt: "/llms.txt existe pero su estructura parece ligera.",
    sitemap:
      "Sitemap encontrado pero no declarado en robots.txt, decláralo para mejor descubrimiento.",
    noJs: "El HTML del servidor tiene poco texto visible. Parte del contenido probablemente requiere JavaScript.",
    schema:
      "Solo se encontró JSON-LD mínimo. Añade Organization/WebSite y Article/FAQ/Product cuando aplique.",
    headings: "La jerarquía tiene problemas menores (saltos de nivel o varios H1).",
    meta: "Faltan algunos metadatos. Title/description/canonical/OG deberían estar todos.",
    semantic:
      "Faltan algunas etiquetas semánticas. Apunta a main, article, section, nav, header, footer.",
    depth: "Profundidad aceptable pero las señales son ligeras, añade FAQ y fecha de publicación.",
    parse:
      "Algunas imágenes no tienen alt o algunos enlaces usan frases vagas como 'haz clic aquí'.",
  },
  fail: {
    aiCrawlers:
      "Los crawlers de IA están bloqueados en robots.txt, los answer engines no pueden leerte.",
    https: "Falla de HTTPS o estado. La página no es accesible de forma limpia sobre HTTPS.",
    llmsTxt: "No hay /llms.txt. Los LLMs no tienen una guía curada de tu contenido más importante.",
    sitemap: "No se encontró sitemap. Los crawlers no pueden descubrir tus URLs eficientemente.",
    noJs: "El HTML del servidor casi no tiene texto visible. El contenido es solo JS e invisible para la mayoría de crawlers.",
    schema:
      "No hay JSON-LD. Los datos estructurados ayudan a los LLMs a extraer hechos sin ambigüedad.",
    headings: "Estructura de encabezados rota (sin H1 o varios H1).",
    meta: "Faltan metadatos críticos (title o description). Open Graph ausente.",
    semantic: "No hay etiquetas semánticas. Los parsers de IA no distinguen layout de contenido.",
    depth: "Contenido escaso, los LLMs necesitan profundidad, autor y fecha para citar una página.",
    parse:
      "La mayoría de imágenes no tienen alt y el texto de enlaces es vago, difícil de parsear.",
  },
  fix: {
    aiCrawlers:
      "Actualiza robots.txt para permitir GPTBot, ChatGPT-User, Google-Extended, ClaudeBot, PerplexityBot y CCBot.",
    https: "Sirve cada URL sobre HTTPS, devuelve 200 y evita redirecciones de varios pasos.",
    llmsTxt:
      "Añade un /llms.txt: un H1 con el nombre del sitio, un resumen de una línea y enlaces Markdown agrupados a tus páginas clave.",
    sitemap: "Genera /sitemap.xml y referéncialo en robots.txt con una línea Sitemap:.",
    noJs: "Renderiza en el servidor o pre-renderiza tu contenido principal para que aparezca sin JavaScript.",
    schema:
      "Añade JSON-LD Schema.org: al menos Organization/WebSite, e idealmente Article, FAQPage o Product según el contenido.",
    headings: "Usa exactamente un H1 por página y anida H2/H3 en orden sin saltar niveles.",
    meta: "Añade <title>, meta description, canonical, html[lang] y etiquetas Open Graph.",
    semantic: "Usa landmarks semánticos: <main>, <article>, <section>, <nav>, <header>, <footer>.",
    depth:
      "Añade una sección de preguntas frecuentes (idealmente con schema FAQPage) y marca fecha con <time datetime>.",
    parse: "Describe imágenes con alt y reemplaza textos vagos de enlace por frases descriptivas.",
  },
  errors: {
    unreachable: "No pudimos acceder a esa URL. Verifica la dirección e intenta de nuevo.",
  },
};

const AI_BOTS = [
  "GPTBot",
  "ChatGPT-User",
  "OAI-SearchBot",
  "Google-Extended",
  "ClaudeBot",
  "anthropic-ai",
  "Claude-Web",
  "PerplexityBot",
  "CCBot",
  "Bytespider",
  "Applebot-Extended",
];

function statusFromScore(score: number): CheckStatus {
  if (score >= 80) return "pass";
  if (score >= 50) return "warning";
  return "fail";
}

function stripTags(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function checkAiCrawlers(robotsText: string | null, copy: AeoCopy): AeoCheck {
  if (!robotsText) {
    // No robots.txt = nothing blocked = pass (default-allow)
    return {
      id: "aiCrawlers",
      groupId: "crawler",
      label: copy.labels.aiCrawlers,
      status: "pass",
      score: 100,
      message: copy.pass.aiCrawlers,
    };
  }
  const lines = robotsText.split(/\r?\n/);
  const blocked = new Set<string>();
  let currentAgents: string[] = [];
  for (const raw of lines) {
    const line = raw.replace(/#.*$/, "").trim();
    if (!line) {
      currentAgents = [];
      continue;
    }
    const m = line.match(/^([A-Za-z-]+)\s*:\s*(.*)$/);
    if (!m) continue;
    const key = m[1].toLowerCase();
    const val = m[2].trim();
    if (key === "user-agent") {
      currentAgents.push(val);
    } else if (key === "disallow" && val === "/") {
      for (const ua of currentAgents) {
        const bot = AI_BOTS.find((b) => b.toLowerCase() === ua.toLowerCase());
        if (bot) blocked.add(bot);
        if (ua === "*") blocked.add("*all*");
      }
    }
  }
  if (blocked.size === 0) {
    return {
      id: "aiCrawlers",
      groupId: "crawler",
      label: copy.labels.aiCrawlers,
      status: "pass",
      score: 100,
      message: copy.pass.aiCrawlers,
    };
  }
  if (blocked.has("*all*")) {
    return {
      id: "aiCrawlers",
      groupId: "crawler",
      label: copy.labels.aiCrawlers,
      status: "fail",
      score: 0,
      message: copy.fail.aiCrawlers,
      fix: copy.fix.aiCrawlers,
    };
  }
  const score = Math.max(20, 100 - blocked.size * 25);
  return {
    id: "aiCrawlers",
    groupId: "crawler",
    label: copy.labels.aiCrawlers,
    status: statusFromScore(score),
    score,
    message: copy.warn.aiCrawlers + " (" + Array.from(blocked).join(", ") + ")",
    fix: copy.fix.aiCrawlers,
  };
}

function checkHttps(finalUrl: string, status: number, redirects: number, copy: AeoCopy): AeoCheck {
  const isHttps = finalUrl.startsWith("https://");
  if (!isHttps) {
    return {
      id: "https",
      groupId: "crawler",
      label: copy.labels.https,
      status: "fail",
      score: 20,
      message: copy.fail.https,
      fix: copy.fix.https,
    };
  }
  if (status >= 200 && status < 300 && redirects <= 1) {
    return {
      id: "https",
      groupId: "crawler",
      label: copy.labels.https,
      status: "pass",
      score: 100,
      message: copy.pass.https,
    };
  }
  if (status >= 200 && status < 400) {
    return {
      id: "https",
      groupId: "crawler",
      label: copy.labels.https,
      status: "warning",
      score: 70,
      message: copy.warn.https,
      fix: copy.fix.https,
    };
  }
  return {
    id: "https",
    groupId: "crawler",
    label: copy.labels.https,
    status: "fail",
    score: 30,
    message: copy.fail.https,
    fix: copy.fix.https,
  };
}

function checkLlmsTxt(text: string | null, copy: AeoCopy): AeoCheck {
  if (!text) {
    return {
      id: "llmsTxt",
      groupId: "crawler",
      label: copy.labels.llmsTxt,
      status: "fail",
      score: 30,
      message: copy.fail.llmsTxt,
      fix: copy.fix.llmsTxt,
    };
  }
  const hasH1 = /^#\s+\S/m.test(text);
  const hasLinks = (text.match(/\]\(\s*\S+\s*\)/g) || []).length >= 2;
  if (hasH1 && hasLinks) {
    return {
      id: "llmsTxt",
      groupId: "crawler",
      label: copy.labels.llmsTxt,
      status: "pass",
      score: 100,
      message: copy.pass.llmsTxt,
    };
  }
  return {
    id: "llmsTxt",
    groupId: "crawler",
    label: copy.labels.llmsTxt,
    status: "warning",
    score: 65,
    message: copy.warn.llmsTxt,
    fix: copy.fix.llmsTxt,
  };
}

function checkSitemap(found: boolean, declaredInRobots: boolean, copy: AeoCopy): AeoCheck {
  if (found && declaredInRobots) {
    return {
      id: "sitemap",
      groupId: "crawler",
      label: copy.labels.sitemap,
      status: "pass",
      score: 100,
      message: copy.pass.sitemap,
    };
  }
  if (found) {
    return {
      id: "sitemap",
      groupId: "crawler",
      label: copy.labels.sitemap,
      status: "warning",
      score: 75,
      message: copy.warn.sitemap,
      fix: copy.fix.sitemap,
    };
  }
  return {
    id: "sitemap",
    groupId: "crawler",
    label: copy.labels.sitemap,
    status: "fail",
    score: 30,
    message: copy.fail.sitemap,
    fix: copy.fix.sitemap,
  };
}

function checkNoJs(visibleChars: number, copy: AeoCopy): AeoCheck {
  if (visibleChars >= 2000) {
    return {
      id: "noJs",
      groupId: "rendering",
      label: copy.labels.noJs,
      status: "pass",
      score: 100,
      message: copy.pass.noJs + ` (~${visibleChars.toLocaleString()} chars).`,
    };
  }
  if (visibleChars >= 600) {
    return {
      id: "noJs",
      groupId: "rendering",
      label: copy.labels.noJs,
      status: "warning",
      score: 70,
      message: copy.warn.noJs + ` (~${visibleChars} chars).`,
      fix: copy.fix.noJs,
    };
  }
  return {
    id: "noJs",
    groupId: "rendering",
    label: copy.labels.noJs,
    status: "fail",
    score: 25,
    message: copy.fail.noJs + ` (~${visibleChars} chars).`,
    fix: copy.fix.noJs,
  };
}

function checkSchema(html: string, copy: AeoCopy): AeoCheck {
  const blocks =
    html.match(/<script\b[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi) ||
    [];
  const types: string[] = [];
  for (const b of blocks) {
    const matches = b.match(/"@type"\s*:\s*"([^"]+)"/g) || [];
    for (const m of matches) {
      const t = m.match(/"([^"]+)"\s*$/);
      if (t) types.push(t[1]);
    }
  }
  if (blocks.length === 0) {
    return {
      id: "schema",
      groupId: "structure",
      label: copy.labels.schema,
      status: "fail",
      score: 30,
      message: copy.fail.schema,
      fix: copy.fix.schema,
    };
  }
  const rich = types.some((t) => /Article|FAQPage|Product|BreadcrumbList|HowTo|Recipe/i.test(t));
  const base = types.some((t) => /Organization|WebSite|LocalBusiness/i.test(t));
  if (rich && base) {
    return {
      id: "schema",
      groupId: "structure",
      label: copy.labels.schema,
      status: "pass",
      score: 100,
      message: copy.pass.schema + ` (${Array.from(new Set(types)).slice(0, 5).join(", ")}).`,
    };
  }
  return {
    id: "schema",
    groupId: "structure",
    label: copy.labels.schema,
    status: "warning",
    score: 70,
    message: copy.warn.schema + ` (${Array.from(new Set(types)).slice(0, 5).join(", ") || ", "}).`,
    fix: copy.fix.schema,
  };
}

function checkHeadings(html: string, copy: AeoCopy): AeoCheck {
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  const all: number[] = [];
  const re = /<h([1-6])\b[^>]*>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    const lvl = Number(m[1]);
    counts[lvl]++;
    all.push(lvl);
  }
  const totalH = all.length;
  const detail = ` (H1: ${counts[1]} · total: ${totalH})`;
  if (counts[1] === 0 || totalH === 0) {
    return {
      id: "headings",
      groupId: "structure",
      label: copy.labels.headings,
      status: "fail",
      score: 30,
      message: copy.fail.headings + detail,
      fix: copy.fix.headings,
    };
  }
  // detect skipped levels (e.g., H1 → H3 with no H2)
  let skipped = false;
  let prev = 0;
  for (const lvl of all) {
    if (prev > 0 && lvl > prev + 1) {
      skipped = true;
      break;
    }
    prev = lvl;
  }
  if (counts[1] === 1 && !skipped) {
    return {
      id: "headings",
      groupId: "structure",
      label: copy.labels.headings,
      status: "pass",
      score: 100,
      message: copy.pass.headings + detail,
    };
  }
  return {
    id: "headings",
    groupId: "structure",
    label: copy.labels.headings,
    status: "warning",
    score: 70,
    message: copy.warn.headings + detail,
    fix: copy.fix.headings,
  };
}

function attrVal(tag: string, name: string): string | null {
  const m =
    tag.match(new RegExp(`\\b${name}\\s*=\\s*"([^"]*)"`, "i")) ||
    tag.match(new RegExp(`\\b${name}\\s*=\\s*'([^']*)'`, "i"));
  return m ? m[1] : null;
}

function checkMeta(html: string, copy: AeoCopy): AeoCheck {
  const hasTitle = /<title\b[^>]*>([^<]+)<\/title>/i.test(html);
  const descMatch = html.match(/<meta\b[^>]*name=["']description["'][^>]*>/i);
  const hasDesc = !!descMatch && !!attrVal(descMatch[0], "content");
  const hasCanonical = /<link\b[^>]*rel=["']canonical["'][^>]*>/i.test(html);
  const htmlTag = html.match(/<html\b[^>]*>/i);
  const lang = htmlTag ? attrVal(htmlTag[0], "lang") : null;
  const ogCount = (html.match(/<meta\b[^>]*property=["']og:[^"']+["'][^>]*>/gi) || []).length;
  const points = [hasTitle, hasDesc, hasCanonical, !!lang, ogCount >= 4].filter(Boolean).length;
  const detail = ` (title: ${hasTitle ? "✓" : "✗"} · desc: ${hasDesc ? "✓" : "✗"} · canonical: ${hasCanonical ? "✓" : "✗"} · lang: ${lang || ", "} · OG: ${ogCount})`;
  if (points === 5) {
    return {
      id: "meta",
      groupId: "structure",
      label: copy.labels.meta,
      status: "pass",
      score: 100,
      message: copy.pass.meta + detail,
    };
  }
  if (points >= 3) {
    return {
      id: "meta",
      groupId: "structure",
      label: copy.labels.meta,
      status: "warning",
      score: 60 + points * 5,
      message: copy.warn.meta + detail,
      fix: copy.fix.meta,
    };
  }
  return {
    id: "meta",
    groupId: "structure",
    label: copy.labels.meta,
    status: "fail",
    score: 30,
    message: copy.fail.meta + detail,
    fix: copy.fix.meta,
  };
}

function checkSemantic(html: string, copy: AeoCopy): AeoCheck {
  const tags = ["main", "article", "section", "nav", "header", "footer"];
  const found = tags.filter((t) => new RegExp(`<${t}\\b`, "i").test(html));
  const detail = ` (${found.length}/${tags.length}: ${found.join(", ") || ", "})`;
  if (found.length >= 5) {
    return {
      id: "semantic",
      groupId: "structure",
      label: copy.labels.semantic,
      status: "pass",
      score: 100,
      message: copy.pass.semantic + detail,
    };
  }
  if (found.length >= 3) {
    return {
      id: "semantic",
      groupId: "structure",
      label: copy.labels.semantic,
      status: "warning",
      score: 70,
      message: copy.warn.semantic + detail,
      fix: copy.fix.semantic,
    };
  }
  return {
    id: "semantic",
    groupId: "structure",
    label: copy.labels.semantic,
    status: "fail",
    score: 30,
    message: copy.fail.semantic + detail,
    fix: copy.fix.semantic,
  };
}

function checkDepth(html: string, visibleText: string, copy: AeoCopy): AeoCheck {
  const wordCount = (visibleText.match(/\S+/g) || []).length;
  const hasFaq = /faq|preguntas\s+frecuentes/i.test(html) || /"@type"\s*:\s*"FAQPage"/i.test(html);
  const hasAuthor =
    /<meta\b[^>]*name=["']author["'][^>]*content=["'][^"']+["']/i.test(html) ||
    /"@type"\s*:\s*"Person"/i.test(html) ||
    /\brel=["']author["']/i.test(html);
  const hasDate =
    /<time\b[^>]*datetime=["'][^"']+["']/i.test(html) ||
    /<meta\b[^>]*property=["']article:published_time["']/i.test(html);
  const checks = [wordCount >= 500, hasFaq, hasAuthor, hasDate].filter(Boolean).length;
  const detail = ` (~${wordCount} words · FAQ: ${hasFaq ? "✓" : "✗"} · Author: ${hasAuthor ? "✓" : "✗"} · Date: ${hasDate ? "✓" : "✗"})`;
  if (checks === 4) {
    return {
      id: "depth",
      groupId: "authority",
      label: copy.labels.depth,
      status: "pass",
      score: 100,
      message: copy.pass.depth + detail,
    };
  }
  if (checks >= 2) {
    return {
      id: "depth",
      groupId: "authority",
      label: copy.labels.depth,
      status: "warning",
      score: 50 + checks * 10,
      message: copy.warn.depth + detail,
      fix: copy.fix.depth,
    };
  }
  return {
    id: "depth",
    groupId: "authority",
    label: copy.labels.depth,
    status: "fail",
    score: 30,
    message: copy.fail.depth + detail,
    fix: copy.fix.depth,
  };
}

const VAGUE_LINK =
  /^(click\s*here|read\s*more|more|here|learn\s*more|aquí|haz\s*clic|leer\s*más|más|click|ver\s*más)$/i;

function checkParseability(html: string, copy: AeoCopy): AeoCheck {
  const imgs = html.match(/<img\b[^>]*>/gi) || [];
  const imgsNoAlt = imgs.filter((t) => {
    const alt = attrVal(t, "alt");
    return alt === null;
  }).length;
  const links = html.match(/<a\b[^>]*>[\s\S]*?<\/a>/gi) || [];
  let vague = 0;
  for (const a of links) {
    const txt = stripTags(a).trim();
    if (txt && VAGUE_LINK.test(txt)) vague++;
  }
  const detail = ` (${imgs.length} images${imgs.length ? `, ${imgsNoAlt} without alt` : ""} · ${links.length} links${vague ? `, ${vague} vague` : ""})`;
  const altRatio = imgs.length === 0 ? 1 : 1 - imgsNoAlt / imgs.length;
  const vagueRatio = links.length === 0 ? 0 : vague / links.length;
  if (altRatio >= 0.9 && vagueRatio <= 0.05) {
    return {
      id: "parse",
      groupId: "parseability",
      label: copy.labels.parse,
      status: "pass",
      score: 100,
      message: copy.pass.parse + detail,
    };
  }
  if (altRatio >= 0.6) {
    return {
      id: "parse",
      groupId: "parseability",
      label: copy.labels.parse,
      status: "warning",
      score: 60 + Math.round(altRatio * 20),
      message: copy.warn.parse + detail,
      fix: copy.fix.parse,
    };
  }
  return {
    id: "parse",
    groupId: "parseability",
    label: copy.labels.parse,
    status: "fail",
    score: 30,
    message: copy.fail.parse + detail,
    fix: copy.fix.parse,
  };
}

async function fetchText(
  u: string,
  timeoutMs = 8000,
): Promise<{
  ok: boolean;
  status: number;
  text: string | null;
  finalUrl: string;
  redirects: number;
}> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(u, {
      redirect: "follow",
      signal: controller.signal,
      headers: {
        "User-Agent":
          "Mozilla/5.0 (compatible; ShugertAEOAudit/1.0; +https://shugert.com.mx/audit)",
        Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
      },
    });
    const text = res.ok ? await res.text() : null;
    const finalUrl = res.url || u;
    // crude redirect count: 1 if final differs from original
    const redirects = finalUrl !== u ? 1 : 0;
    return { ok: res.ok, status: res.status, text, finalUrl, redirects };
  } catch {
    return { ok: false, status: 0, text: null, finalUrl: u, redirects: 0 };
  } finally {
    clearTimeout(timer);
  }
}

export const runAeoAudit = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      url: z.string().trim().min(1).max(2048),
      locale: z.enum(["en", "es"]).default("en"),
      runId: z.string().uuid().optional(),
      form_started_at: z.number().int().positive(),
      website_confirmation: z.string().max(200).optional().or(z.literal("")),
      turnstile_token: z.string().max(4000).optional().or(z.literal("")),
    }).parse,
  )
  .handler(async ({ data }) => {
    await assertPublicFormSecurity({
      form: "audit_aeo",
      input: data,
      minimumMs: 800,
      maxRequests: 6,
      windowMs: 60 * 60 * 1000,
    });
    const copy = data.locale === "es" ? AEO_ES : AEO_EN;
    const started = Date.now();

    const normalized = normalizeUrl(data.url);
    if (!normalized) throw new Error(copy.errors.unreachable);
    let parsed: URL;
    try {
      parsed = new URL(normalized);
    } catch {
      throw new Error(copy.errors.unreachable);
    }
    const origin = `${parsed.protocol}//${parsed.host}`;

    const [pageRes, robotsRes, llmsRes, sitemapRes] = await Promise.all([
      fetchText(normalized),
      fetchText(`${origin}/robots.txt`, 5000),
      fetchText(`${origin}/llms.txt`, 5000),
      fetchText(`${origin}/sitemap.xml`, 5000),
    ]);

    if (!pageRes.ok || !pageRes.text) {
      throw new Error(copy.errors.unreachable);
    }

    const html = pageRes.text;
    const visibleText = stripTags(html);
    const sitemapDeclared = robotsRes.text ? /^\s*Sitemap\s*:/im.test(robotsRes.text) : false;

    const checks: AeoCheck[] = [
      checkAiCrawlers(robotsRes.text, copy),
      checkHttps(pageRes.finalUrl, pageRes.status, pageRes.redirects, copy),
      checkLlmsTxt(llmsRes.ok ? llmsRes.text : null, copy),
      checkSitemap(sitemapRes.ok, sitemapDeclared, copy),
      checkNoJs(visibleText.length, copy),
      checkSchema(html, copy),
      checkHeadings(html, copy),
      checkMeta(html, copy),
      checkSemantic(html, copy),
      checkDepth(html, visibleText, copy),
      checkParseability(html, copy),
    ];

    const overallScore = Math.round(checks.reduce((s, c) => s + c.score, 0) / checks.length);
    const scoreLabel: AeoResult["scoreLabel"] =
      overallScore >= 80 ? "good" : overallScore >= 60 ? "ok" : "bad";

    const groupIds: AeoGroupId[] = [
      "crawler",
      "rendering",
      "structure",
      "authority",
      "parseability",
    ];
    const groups: AeoGroup[] = groupIds.map((id) => ({ id, label: copy.groups[id] }));

    const result: AeoResult = {
      url: pageRes.finalUrl,
      overallScore,
      scoreLabel,
      groups,
      checks,
      fetchedMs: Date.now() - started,
    };

    if (data.runId) {
      await persistAuditRun({
        runId: data.runId,
        inputUrl: data.url,
        normalizedUrl: pageRes.finalUrl,
        locale: data.locale,
        storeScore: null,
        storeResult: null,
        storeDataQuality: null,
        aeoScore: overallScore,
        aeoResult: result as unknown as Record<string, unknown>,
        aeoDataQuality: "live",
      });
    }

    return result;
  });

// ---------- Persistence + public read for /audit/results/$slug ----------

interface PersistArgs {
  runId: string;
  inputUrl: string;
  normalizedUrl: string;
  locale: "en" | "es";
  storeScore: number | null;
  storeResult: Record<string, unknown> | null;
  storeDataQuality: string | null;
  aeoScore: number | null;
  aeoResult: Record<string, unknown> | null;
  aeoDataQuality: string | null;
  // Defaults preserve the public /audit path's existing behavior
  // (is_public=true, source='public_form', throttled). Only the internal
  // batch entry point overrides these.
  isPublic?: boolean;
  source?: string;
  skipThrottle?: boolean;
}

function getClientIp(req: Request): string | null {
  const h = req.headers;
  const cf = h.get("cf-connecting-ip");
  if (cf) return cf.trim();
  const xff = h.get("x-forwarded-for");
  if (xff) return xff.split(",")[0]?.trim() ?? null;
  const real = h.get("x-real-ip");
  return real ? real.trim() : null;
}

async function hashIp(ip: string | null): Promise<string | null> {
  if (!ip) return null;
  const daySalt = new Date().toISOString().slice(0, 10);
  return sha256Hex(`${ip}|shugert-audit|${daySalt}`);
}

async function persistAuditRun(args: PersistArgs): Promise<void> {
  try {
    const normalized =
      normalizeAuditHostname(args.normalizedUrl) ?? normalizeAuditHostname(args.inputUrl);
    if (!normalized) return;

    let ip: string | null = null;
    let userAgent: string | null = null;
    try {
      const req = getRequest();
      if (req) {
        ip = getClientIp(req);
        userAgent = req.headers.get("user-agent");
      }
    } catch {
      // getRequest may not be available in every context; safe to skip
    }

    const ipHash = await hashIp(ip);

    const rpcArgs = {
      _run_id: args.runId,
      _slug: normalized.slug,
      _hostname: normalized.hostname,
      _input_url: args.inputUrl.slice(0, 500),
      _locale: args.locale,
      _ip_hash: ipHash,
      _user_agent: userAgent ? userAgent.slice(0, 500) : null,
      _store_score: args.storeScore,
      _store_result: args.storeResult,
      _store_data_quality: args.storeDataQuality,
      _aeo_score: args.aeoScore,
      _aeo_result: args.aeoResult,
      _aeo_data_quality: args.aeoDataQuality,
      _is_public: args.isPublic ?? true,
      _source: args.source ?? "public_form",
      _skip_throttle: args.skipThrottle ?? false,
    };
    // Supabase's generated RPC typings model every function argument as
    // non-null; our persist_audit_run tolerates nulls on every column that
    // this half doesn't set. Cast is intentional.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabaseAdmin.rpc("persist_audit_run", rpcArgs as any);
    if (error) {
      console.warn("persist_audit_run failed:", error.message);
    }
  } catch (err) {
    console.warn("persist_audit_run threw:", err instanceof Error ? err.message : err);
  }
}

// Combined quality is derived at read time from the two persisted halves.
export type CombinedDataQuality = "complete" | "partial" | "fallback";

export interface PublicAuditRun {
  runId: string;
  slug: string;
  hostname: string;
  inputUrl: string;
  locale: "en" | "es";
  auditedAt: string;
  storeScore: number | null;
  aeoScore: number | null;
  storeResult: AuditResult | null;
  aeoResult: AeoResult | null;
  storeDataQuality: string | null;
  aeoDataQuality: string | null;
  combinedDataQuality: CombinedDataQuality;
  excludedIds: string[];
}

function deriveCombinedQuality(store: string | null, aeo: string | null): CombinedDataQuality {
  if (store === "fallback" || aeo === "fallback") return "fallback";
  if (store === null || aeo === null) return "partial";
  // Both present and neither is fallback → treat as complete.
  return "complete";
}

export const getPublicAuditRun = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      slug: z.string().trim().min(1).max(255),
      locale: z.enum(["en", "es"]).default("en"),
    }).parse,
  )
  .handler(async ({ data }): Promise<PublicAuditRun | null> => {
    const { data: pointer, error: pErr } = await supabaseAdmin
      .from("audit_run_latest")
      .select("run_id")
      .eq("slug", data.slug)
      .eq("locale", data.locale)
      .maybeSingle();
    if (pErr) {
      console.warn("[getPublicAuditRun] miss", {
        slug: data.slug,
        locale: data.locale,
        reason: "pointer_lookup_error",
        error: pErr.message,
      });
      return null;
    }
    if (!pointer) {
      console.warn("[getPublicAuditRun] miss", {
        slug: data.slug,
        locale: data.locale,
        reason: "pointer_missing",
      });
      return null;
    }

    const { data: run, error: rErr } = await supabaseAdmin
      .from("audit_runs")
      .select("*")
      .eq("id", pointer.run_id)
      .eq("is_public", true)
      .maybeSingle();
    if (rErr) {
      console.warn("[getPublicAuditRun] miss", {
        slug: data.slug,
        locale: data.locale,
        reason: "run_read_error",
        runId: pointer.run_id,
        error: rErr.message,
      });
      return null;
    }
    if (!run) {
      console.warn("[getPublicAuditRun] miss", {
        slug: data.slug,
        locale: data.locale,
        reason: "run_missing_or_not_public",
        runId: pointer.run_id,
      });
      return null;
    }

    console.log("[getPublicAuditRun] hit", { slug: data.slug, locale: data.locale, runId: run.id });

    return {
      runId: run.id,
      slug: run.slug,
      hostname: run.hostname,
      inputUrl: run.input_url,
      locale: run.locale as "en" | "es",
      auditedAt: run.created_at,
      storeScore: run.store_score,
      aeoScore: run.aeo_score,
      storeResult: (run.store_result as AuditResult | null) ?? null,
      aeoResult: (run.aeo_result as AeoResult | null) ?? null,
      storeDataQuality: run.store_data_quality,
      aeoDataQuality: run.aeo_data_quality,
      combinedDataQuality: deriveCombinedQuality(run.store_data_quality, run.aeo_data_quality),
      excludedIds: ((run as { excluded_ids?: string[] | null }).excluded_ids ?? []) as string[],
    };
  });

// ---------- Public AEO leaderboard ----------
// One row per (slug, locale) via audit_run_latest → audit_runs. is_public=true
// respects the admin kill switch. Volume is small enough to sort in JS.
export const getLeaderboard = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      locale: z.enum(["en", "es"]).default("en"),
    }).parse,
  )
  .handler(async ({ data }): Promise<LeaderboardData> => {
    const { data: rows, error } = await supabaseAdmin
      .from("audit_run_latest")
      .select(
        "slug, locale, audit_runs!inner(id, slug, hostname, locale, aeo_score, store_score, created_at, is_public)",
      )
      .eq("locale", data.locale)
      .eq("audit_runs.is_public", true)
      .eq("audit_runs.locale", data.locale)
      .gte("audit_runs.aeo_score", LEADERBOARD_MIN_AEO_SCORE);

    if (error) {
      console.warn("[getLeaderboard] query error", { locale: data.locale, error: error.message });
      return { entries: [], qualifying: 0 };
    }

    type RunRow = {
      id: string;
      slug: string;
      hostname: string;
      locale: string;
      aeo_score: number | null;
      store_score: number | null;
      created_at: string;
      is_public: boolean;
    };

    const runs: RunRow[] = ((rows ?? []) as Array<{ audit_runs: RunRow | RunRow[] | null }>)
      .flatMap((r) =>
        Array.isArray(r.audit_runs) ? r.audit_runs : r.audit_runs ? [r.audit_runs] : [],
      )
      .filter((r): r is RunRow => !!r && r.aeo_score !== null);

    runs.sort((a, b) => {
      const byAeo = (b.aeo_score ?? 0) - (a.aeo_score ?? 0);
      if (byAeo !== 0) return byAeo;
      const byStore = (b.store_score ?? -1) - (a.store_score ?? -1);
      if (byStore !== 0) return byStore;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });

    const qualifying = runs.length;
    const entries: LeaderboardEntry[] = runs.slice(0, LEADERBOARD_TOP_N).map((r, i) => ({
      rank: i + 1,
      slug: r.slug,
      hostname: r.hostname,
      aeoScore: r.aeo_score as number,
      storeScore: r.store_score,
      auditedAt: r.created_at,
    }));

    return { entries, qualifying };
  });

// ---------- Admin: manage stored audit runs ----------

export interface AdminAuditRunRow {
  id: string;
  slug: string;
  hostname: string;
  input_url: string;
  locale: "en" | "es";
  store_score: number | null;
  aeo_score: number | null;
  is_public: boolean;
  is_pointer: boolean;
  source: string;
  created_at: string;
  excluded_ids: string[];
  available_checks: {
    store: { id: string; label: string; status: CheckStatus; weight: number }[];
    aeo: { id: string; label: string; status: CheckStatus; score: number }[];
  };
}

async function assertAdmin(userId: string) {
  const { data: isAdmin, error } = await supabaseAdmin.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!isAdmin) throw new Error("Forbidden: admin role required");
}

export const listAuditRunsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ runs: AdminAuditRunRow[] }> => {
    await assertAdmin(context.userId);
    const { data: runs, error } = await supabaseAdmin
      .from("audit_runs")
      .select(
        "id, slug, hostname, input_url, locale, store_score, aeo_score, is_public, source, created_at, excluded_ids, store_result, aeo_result",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    const { data: pointers } = await supabaseAdmin.from("audit_run_latest").select("run_id");
    const pointerSet = new Set((pointers ?? []).map((p) => p.run_id));
    return {
      runs: (runs ?? []).map((r) => {
        const rr = r as typeof r & {
          excluded_ids?: string[] | null;
          store_result?: AuditResult | null;
          aeo_result?: AeoResult | null;
          source?: string | null;
        };
        return {
          id: rr.id,
          slug: rr.slug,
          hostname: rr.hostname,
          input_url: rr.input_url,
          locale: rr.locale as "en" | "es",
          store_score: rr.store_score,
          aeo_score: rr.aeo_score,
          is_public: rr.is_public,
          is_pointer: pointerSet.has(rr.id),
          source: rr.source ?? "public_form",
          created_at: rr.created_at,
          excluded_ids: rr.excluded_ids ?? [],
          available_checks: {
            store: (rr.store_result?.checks ?? []).map((c) => ({
              id: c.id,
              label: c.label,
              status: c.status,
              weight: c.weight,
            })),
            aeo: (rr.aeo_result?.checks ?? []).map((c) => ({
              id: c.id,
              label: c.label,
              status: c.status,
              score: c.score,
            })),
          },
        };
      }),
    };
  });

export const setAuditRunVisibility = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    z.object({
      id: z.string().uuid(),
      isPublic: z.boolean(),
    }).parse,
  )
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin
      .from("audit_runs")
      .update({ is_public: data.isPublic })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteAuditRun = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ id: z.string().uuid() }).parse)
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    await assertAdmin(context.userId);
    // Grab slug/locale before delete so we can restore a fallback pointer.
    const { data: run, error: readErr } = await supabaseAdmin
      .from("audit_runs")
      .select("slug, locale")
      .eq("id", data.id)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    const { error: delErr } = await supabaseAdmin.from("audit_runs").delete().eq("id", data.id);
    if (delErr) throw new Error(delErr.message);

    // ON DELETE CASCADE removes the pointer row if this run was the pointer.
    // The sync trigger only fires on INSERT/UPDATE, so restore a fallback here.
    if (run) {
      const { data: existing } = await supabaseAdmin
        .from("audit_run_latest")
        .select("run_id")
        .eq("slug", run.slug)
        .eq("locale", run.locale)
        .maybeSingle();
      if (!existing) {
        const { data: fallback } = await supabaseAdmin
          .from("audit_runs")
          .select("id")
          .eq("slug", run.slug)
          .eq("locale", run.locale)
          .eq("is_public", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (fallback) {
          await supabaseAdmin.from("audit_run_latest").insert({
            slug: run.slug,
            locale: run.locale,
            run_id: fallback.id,
          });
        }
      }
    }
    return { ok: true };
  });

// ---------- Admin: exclude checks from score ----------

function recomputeStoreScore(result: AuditResult | null, excluded: Set<string>): number | null {
  if (!result) return null;
  const kept = result.checks.filter((c) => !excluded.has(c.id));
  if (kept.length === 0) return null;
  return calcOverall(kept);
}

function recomputeAeoScore(result: AeoResult | null, excluded: Set<string>): number | null {
  if (!result) return null;
  const kept = result.checks.filter((c) => !excluded.has(c.id));
  if (kept.length === 0) return null;
  return Math.round(kept.reduce((s, c) => s + c.score, 0) / kept.length);
}

export const setAuditRunExclusions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    z.object({
      id: z.string().uuid(),
      excludedIds: z.array(z.string().min(1).max(100)).max(100),
    }).parse,
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{
      ok: true;
      storeScore: number | null;
      aeoScore: number | null;
    }> => {
      await assertAdmin(context.userId);
      const { data: run, error: readErr } = await supabaseAdmin
        .from("audit_runs")
        .select("store_result, aeo_result")
        .eq("id", data.id)
        .maybeSingle();
      if (readErr) throw new Error(readErr.message);
      if (!run) throw new Error("Run not found");

      const excluded = new Set(data.excludedIds);
      const storeScore = recomputeStoreScore(
        (run.store_result as AuditResult | null) ?? null,
        excluded,
      );
      const aeoScore = recomputeAeoScore((run.aeo_result as AeoResult | null) ?? null, excluded);

      const { error: upErr } = await supabaseAdmin
        .from("audit_runs")
        .update({
          excluded_ids: data.excludedIds,
          store_score: storeScore,
          aeo_score: aeoScore,
        })
        .eq("id", data.id);
      if (upErr) throw new Error(upErr.message);

      return { ok: true, storeScore, aeoScore };
    },
  );
